import { Alpine } from '../../core/alpine-core.js';
import { viteEnv } from '../../core/import-meta-env.js';
import { preferredAppOrigin } from '../../core/preferred-app-origin.js';
import { resolveVenueKioskAppOrigin } from '../../public/kiosk-tablet-api.js';
import { requestScorerHaptic } from '../../shared/scorer-haptic-bridge.js';
import QRCode from 'qrcode';
import { apiFetch, getAccessToken, LS_VENUE_KIOSK_FIXTURE_EMBED_TOKEN } from '../api.js';
import {
  SCORER_QUICK_PRESET_POOL,
  buildKeypadQuickOverlaySlots,
  defaultScorerQuickConfig,
  isNsQuickPresetValue,
  loadScorerQuickConfig,
  mergeMoreQuickPresets,
  normalizeScorerQuickConfig,
  quickPresetButtonLabel,
  saveScorerQuickConfig,
  withNearCheckoutNsOnFourthQuickTop,
} from '../scorer-quick-config.js';
import {
  SCORER_PLAYER_STATS_CAROUSEL_STATE,
  createScorerPlayerStatsCarouselMethods,
} from '../../shared/scorer-player-stats-carousel.js';
import { createScorerMatchScoreboardBindings } from '../../shared/scorer-match-scoreboard.js';
import { createScorerCoreMethods } from '../../shared/scorer-core.js';
import { kioskDeviceId } from '../../public/kiosk-tablet-api.js';

const FIXTURE_SCORER_QUICK_LS = 'pdl.fixture-scorer.quick';
import {
  acknowledgeFixtureLiveEvents,
  buildLocalEvent,
  createFixtureLiveSession,
  enqueueFixtureLiveEvent,
  saveFixtureLiveSession,
  loadFixtureLiveSession,
} from '../../core/fixture-live-scoring.js';

function parseFixtureId() {
  const parts = location.pathname.split('/').filter(Boolean);
  let idx = parts.indexOf('fixture-score');
  if (idx >= 0 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
  idx = parts.indexOf('fixtures');
  if (idx >= 0 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
  return null;
}

/** True when this navigation is a reload (F5 / refresh) — do not beacon-release the scoring lock. */
function isNavigationReloadNav() {
  try {
    const n = performance.getEntriesByType?.('navigation')?.[0];
    if (n && n.type === 'reload') return true;
  } catch { }
  try {
    const nav = performance.navigation;
    if (nav && nav.type === nav.TYPE_RELOAD) return true;
  } catch { }
  return false;
}

/**
 * Fire-and-forget release so other devices see no controller within seconds when this tab closes.
 * Uses sendBeacon / fetch keepalive (Authorization via query matches auth middleware).
 */
function beaconReleaseFixtureSessionControl(sessionId, deviceId, deviceLabel) {
  const token = getAccessToken();
  if (!sessionId || !deviceId || !token) return;
  const qs = `?access_token=${encodeURIComponent(token)}`;
  const withApiPrefix = `/api/fixture-sessions/${encodeURIComponent(String(sessionId))}/control/release${qs}`;
  const base = String(window.__API_BASE__ || '').replace(/\/+$/, '');
  const url = base ? `${base}${withApiPrefix}` : `${window.location?.origin || ''}${withApiPrefix}`;
  if (!/^https?:\/\//i.test(url)) return;
  const body = JSON.stringify({ deviceId, deviceLabel: String(deviceLabel || '') });
  let sent = false;
  if (typeof navigator.sendBeacon === 'function') {
    try {
      sent = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } catch { }
  }
  if (!sent) {
    try {
      fetch(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => { });
    } catch { }
  }
}

function isScoringWindowMode() {
  try {
    return ['1', 'true', 'yes'].includes(String(new URLSearchParams(window.location.search).get('window') || '').toLowerCase());
  } catch {
    return false;
  }
}

/** Phone/tablet opened from venue kiosk QR (`embed=1&window=1` on fixture-score route). */
function isVenueKioskFixtureQrScorerPath() {
  try {
    const p = String(globalThis.location?.pathname || '');
    if (!/^\/venue-kiosk\/fixture-score\//i.test(p)) return false;
    const sp = new URLSearchParams(String(globalThis.location?.search || ''));
    return sp.get('embed') === '1' && isScoringWindowMode();
  } catch {
    return false;
  }
}

function scrollWindowToTop() {
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  } catch {
    window.scrollTo(0, 0);
  }
}

function routerNavigate(path) {
  const router = window.PineconeRouter;
  if (router?.navigate) router.navigate(path);
  else window.location.href = path;
}

const FIXTURE_SCORER_AVATAR_FALLBACK = '/assets/pdl-avatar-dark.svg';

function resolvePlayerAvatarUrl(player) {
  if (!player) return FIXTURE_SCORER_AVATAR_FALLBACK;
  const url = String(player.imageUrl || player.photo || player.avatar || player.image || '').trim();
  return url || FIXTURE_SCORER_AVATAR_FALLBACK;
}

function readFixtureKioskTokenFromUrl() {
  try {
    const sp = new URLSearchParams(String(globalThis.location?.search || ''));
    return String(sp.get('kiosk') || sp.get('kioskToken') || '').trim();
  } catch {
    return '';
  }
}

function persistFixtureKioskToken(token) {
  const t = String(token || '').trim();
  if (!t) return;
  try {
    window.localStorage.setItem(LS_VENUE_KIOSK_FIXTURE_EMBED_TOKEN, t);
  } catch {
    /* ignore */
  }
}

function makeDeviceId(fixtureId) {
  try {
    const key = `pdl.fixture-controller-id.${fixtureId}`;
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const created = `fixture-${fixtureId}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(key, created);
    return created;
  } catch {
    return `fixture-${fixtureId}-controller`;
  }
}

Alpine.data('fixtureScore', () => ({
  ...SCORER_PLAYER_STATS_CAROUSEL_STATE,
  ...createScorerCoreMethods({ scorerContext: 'fixture' }),
  fixtureId: '',
  fixture: null,
  session: null,
  localSession: null,
  eventSource: null,
  eventStreamHealthy: false,
  eventStreamReconnectTimer: null,
  heartbeatTimer: null,
  replayTimer: null,
  replayBusy: false,
  recoveryNotice: '',
  releaseControlBeaconSent: false,
  handlePageHideForControl: null,
  deviceId: '',
  deviceLabel: '',
  loading: true,
  saving: false,
  error: '',
  scoreInput: '',
  allPlayers: [],
  teams: [],
  borrowPicker: {
    open: false,
    side: 'home',
    search: '',
    target: null,
  },
  selectedHomePlayers: [],
  selectedAwayPlayers: [],
  activeThrower: { home: '', away: '' },
  syncedScoresheet: null,
  editingVisitIndex: -1,
  checkoutDarts: '',
  checkoutConfirmOpen: false,
  checkoutConfirmPending: null,
  checkoutConfirmDarts: '3',
  /** For master-out: server `finishType` (`double` | `triple` | `master`). */
  masterFinishType: 'master',
  scoringWindow: false,
  playersSaveTimer: null,
  fullscreenActive: false,
  scorerHeaderCompact: false,
  /** Dedicated scorer: narrow viewport uses tighter top bar padding (see scorerTopBarRowPaddingStyle). */
  scorerMobileTopBar: false,
  quickScoresExpanded: false,
  quickMoreOpen: false,
  quickMoreFixedPopover: true,
  quickMorePanelStyle: '',
  _keypadUiPointerTs: 0,
  _keypadUiPointerEl: null,
  _quickMoreSuppressOutsideUntil: 0,
  quickScoreTop: [],
  quickScoreMore: [],
  quickConfigOpen: false,
  jumpMenuOpen: false,
  scoresheetSyncing: false,
  undoArmed: false,
  undoArmTimer: null,
  forceScoringWindow: false,
  scorerQrCanvas: null,
  setupTab: 'singles',
  setupAssignmentsEditMode: false,
  setupSaveTimer: null,
  fixtureScorerAsideOpen: false,
  pendingSetupSaves: {},
  setupSaveState: 'idle',
  setupSaveStateTimer: null,
  tvPairCode: '',
  tvPairBusy: false,
  tvPairError: '',
  /** Match Console: collapse Fixture Setup card after lineups are complete (captain gate or live). */
  fixtureSetupHubCollapsed: false,
  handleAlpineDestroy: null,
  /** Venue kiosk iframe (`?embed=1`): compact chrome, no portrait gate. */
  embedMode: false,
  /** `?from=venue-kiosk` when opened from the venue tablet. */
  fromVenueKiosk: false,
  venueEmbedLeaveDialogOpen: false,
  kioskHubSwitchConfirmOpen: false,
  kioskHubSwitchTargetIndex: -1,
  venueRailExpanded: false,
  _vkShellMessageListener: null,

  initScorerWindow() {
    this.forceScoringWindow = true;
    return this.init();
  },

  async init() {
    if (!this.forceScoringWindow && !isScoringWindowMode()) {
      const legacyScore = /\/fixtures\/[^/]+\/score\/?$/i.test(String(location.pathname || ''));
      if (legacyScore) {
        routerNavigate(String(location.pathname).replace(/\/score\/?$/, '/setup') + String(location.search || ''));
        return;
      }
    }

    let embed = false;
    try {
      const u = new URL(window.location.href);
      embed = u.searchParams.get('embed') === '1';
      this.fromVenueKiosk = u.searchParams.get('from') === 'venue-kiosk';
      if (embed && !this.fromVenueKiosk && globalThis.parent !== globalThis) {
        try {
          const loc = globalThis.parent.location;
          const base = `${String(loc?.pathname || '')}${String(loc?.search || '')}`;
          if (base.includes('venue-kiosk')) this.fromVenueKiosk = true;
        } catch {
          /* cross-origin parent */
        }
      }
    } catch {
      embed = false;
      this.fromVenueKiosk = false;
    }
    this.embedMode = embed;
    this._vkShellMessageListener = (ev) => {
      try {
        if (!ev?.data || typeof ev.data !== 'object') return;
        const origin = String(globalThis.location?.origin || '');
        if (origin && String(ev.origin || '') !== origin) return;
        const d = ev.data;
        if (this.handleVenueKioskScorerThemeMessage?.(d)) return;
        if (this.handleVenueKioskShellHistoryMessage?.(d)) return;
        if (d.source === 'venue-kiosk-shell' && d.type === 'venue-kiosk-rail-state') {
          this.fromVenueKiosk = true;
          if (typeof d.expanded === 'boolean') this.venueRailExpanded = d.expanded;
        }
      } catch {
        /* ignore */
      }
    };
    globalThis.window?.addEventListener('message', this._vkShellMessageListener);

    this.applyScorerLayoutTheme(this.readScorerLayoutTheme());
    const q0 = loadScorerQuickConfig(FIXTURE_SCORER_QUICK_LS);
    this.quickScoreTop = [...q0.top];
    this.quickScoreMore = [...q0.more];
    this.ensureQuickScoreDefaults();

    if (
      !this.venueKioskFixtureHubMode() &&
      !isVenueKioskFixtureQrScorerPath() &&
      !this.$store.features.enabled('liveFixtures')
    ) {
      this.error = 'Live fixture scoring is disabled.';
      this.loading = false;
      return;
    }
    this.fixtureId = parseFixtureId() || '';
    if (!this.fixtureId) {
      this.error = 'Missing fixture id';
      this.loading = false;
      return;
    }
    this.scoringWindow = !!this.forceScoringWindow || isScoringWindowMode() || (this.embedMode && !this.fromVenueKiosk);
    this.applyDedicatedChromeMode(this.scoringWindow);
    this.syncScorerTopBarViewport();
    try {
      if ('scrollRestoration' in window.history) {
        this.previousScrollRestoration = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
      }
    } catch { }
    scrollWindowToTop();
    this.handleFullscreenChange = () => {
      this.fullscreenActive = !!document.fullscreenElement;
    };
    this.handleOnline = () => {
      this.flushPendingEvents();
      this.ensureEventStream();
    };
    this.handleScroll = () => {
      this.scorerHeaderCompact = window.scrollY > 24;
    };
    this.handleResize = () => {
      this.syncScorerTopBarViewport();
    };
    if (this.handleKeydown) {
      window.removeEventListener('keydown', this.handleKeydown, true);
    }
    this.handleKeydown = (event) => {
      this.onScoreKeydown(event);
    };
    this.handleAlpineDestroy = () => this.teardown();
    try {
      this.$el?.addEventListener?.('alpine:destroy', this.handleAlpineDestroy);
    } catch { }
    this.deviceId = this.fromVenueKiosk ? kioskDeviceId() : makeDeviceId(this.fixtureId);
    this.deviceLabel = this.fromVenueKiosk ? 'Venue kiosk' : `${navigator.platform || 'Device'} scorer`;
    const kioskFromUrl = readFixtureKioskTokenFromUrl();
    if (kioskFromUrl) persistFixtureKioskToken(kioskFromUrl);
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('keydown', this.handleKeydown, true);
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    window.addEventListener('beforeunload', () => this.teardown());
    this.handlePageHideForControl = (ev) => {
      if (ev?.persisted) return;
      this.maybeBeaconReleaseControlOnUnload();
    };
    window.addEventListener('pagehide', this.handlePageHideForControl);
    try {
    await this.loadFixture();
    if (this.fromVenueKiosk) {
      this.hydrateTeamsFromFixture();
      this.hydratePlayersFromFixture();
    } else if (this.venueKioskFixtureHubMode()) {
      this.hydrateTeamsFromFixture();
      this.hydratePlayersFromFixture();
    } else {
      await Promise.all([this.loadTeams(), this.loadBorrowablePlayers()]);
    }
    await this.startOrResumeSession();
    if (['completed', 'submitted', 'verified'].includes(String(this.session?.status || '').toLowerCase())) {
      this.refreshCompletionScoresheetDraft();
    }
    this.scorerHeaderCompact = window.scrollY > 24;
    scrollWindowToTop();
    this.loading = false;
    window.requestAnimationFrame(() => {
      scrollWindowToTop();
      if (!this.embedMode) this.renderScorerQr();
    });
  } catch (e) {
    const stale = /not found|fixture_not_found|\b404\b/i.test(String(e?.message || ''));
    if (stale && this.embedMode && this.fromVenueKiosk) {
      this.postVenueKioskShellMessage({ type: 'fixture-scorer-leave', stale: true });
    } else if (stale && this.isMobileFixtureQrScorer()) {
      this.openRemoteVenueScoringEnded('abandoned');
      this.error = '';
    } else {
      this.error = e?.message || 'Unable to load fixture scorer';
    }
    this.loading = false;
  }
  },

  postVenueKioskShellMessage(payload) {
    const origin = globalThis.location?.origin || '*';
    try {
      if (globalThis.parent !== globalThis) {
        globalThis.parent.postMessage({ source: 'darts-scorer-embed', ...payload }, origin);
      }
    } catch {
      /* ignore */
    }
  },

  isVenueKioskEmbed() {
    if (!this.embedMode) return false;
    if (this.fromVenueKiosk) return true;
    try {
      return globalThis.parent !== globalThis;
    } catch {
      return false;
    }
  },

  toggleVenueKioskShellRailFromEmbed() {
    if (!this.isVenueKioskEmbed()) return;
    const nextExpanded = !this.venueRailExpanded;
    this.venueRailExpanded = nextExpanded;
    this.postVenueKioskShellMessage({ type: 'toggle-venue-kiosk-rail', expanded: nextExpanded });
  },

  canVenueEmbedAbandonFixture() {
    if (this.isMobileFixtureQrScorer()) return false;
    return !!(
      this.isVenueKioskEmbed()
      && this.scoringWindow
      && !this.fixtureSessionIsFinalized()
    );
  },

  onRemoteVenueScoringEnded() {
    this.disconnectEventStream();
  },

  activeSidePlayerCount(side) {
    const game = this.currentGame();
    if (!game) return 1;
    const type = String(game?.type || 'singles');
    if (type === 'beer') return this.beerPlayerIds(side).length || 4;
    if (type === 'doubles') return 2;
    return 1;
  },

  sidePlayers(side) {
    const game = this.currentGame();
    if (!game) return [];
    const type = String(game?.type || 'singles');
    let ids = [];
    if (type === 'beer') {
      ids = this.beerPlayerIds(side);
    } else {
      ids = ((side === 'home' ? game?.homePlayers : game?.awayPlayers) || [])
        .map((id) => String(id || ''))
        .filter(Boolean)
        .slice(0, this.expectedPlayers());
    }
    return ids.map((id) => ({
      id,
      playerId: id,
      name: this.playerName(id),
      avatarUrl: resolvePlayerAvatarUrl(this.allPlayers?.find?.((p) => String(p?._id || p?.id) === id)),
    }));
  },

  currentTurnId() {
    const side = this.currentTurnSide();
    return String(this.activeThrower?.[side] || this.defaultThrowerFor(side) || '');
  },

  openVenueEmbedLeaveDialog() {
    if (!this.embedMode || !this.fromVenueKiosk) return;
    this.venueEmbedLeaveDialogOpen = true;
  },

  closeVenueEmbedLeaveDialog() {
    this.venueEmbedLeaveDialogOpen = false;
  },

  scorerAbandonDialogCopy() {
    if (this.scoringWindow && this.embedMode && this.fromVenueKiosk) {
      return {
        title: 'Leave live scoring?',
        body: 'Return to the match program. Progress is saved — tap a game when you want to score again.',
        confirm: 'Back to program',
      };
    }
    return {
      title: 'Return to Fixtures?',
      body: 'You will leave this scorer on the kiosk. The fixture session stays on the server; open it again from Fixtures when you are ready.',
      confirm: 'Yes, leave',
    };
  },

  confirmVenueEmbedLeaveToFixtures() {
    if (!this.embedMode || !this.fromVenueKiosk) return;
    this.venueEmbedLeaveDialogOpen = false;
    if (this.scoringWindow) {
      this.returnVenueKioskFixtureHub();
      return;
    }
    this.postVenueKioskShellMessage({ type: 'fixture-scorer-leave' });
  },

  venueKioskHomeHref() {
    return `${resolveVenueKioskAppOrigin()}/venue-kiosk/fixtures`;
  },

  maybeBeaconReleaseControlOnUnload() {
    if (this.releaseControlBeaconSent) return;
    if (!this.session?._id || !this.isControllerOwner()) return;
    if (this.fixtureSessionIsFinalized()) return;
    if (isNavigationReloadNav()) return;
    this.releaseControlBeaconSent = true;
    beaconReleaseFixtureSessionControl(this.session._id, this.deviceId, this.deviceLabel);
  },

  teardown() {
    this.maybeBeaconReleaseControlOnUnload();
    if (this.handlePageHideForControl) {
      try {
        window.removeEventListener('pagehide', this.handlePageHideForControl);
      } catch { }
      this.handlePageHideForControl = null;
    }
    if (this._vkShellMessageListener) {
      try {
        globalThis.window?.removeEventListener('message', this._vkShellMessageListener);
      } catch { }
      this._vkShellMessageListener = null;
    }
    this.applyDedicatedChromeMode(false);
    try {
      if ('scrollRestoration' in window.history && this.previousScrollRestoration) {
        window.history.scrollRestoration = this.previousScrollRestoration;
      }
    } catch { }
    if (this.handleAlpineDestroy && this.$el) {
      try {
        this.$el.removeEventListener('alpine:destroy', this.handleAlpineDestroy);
      } catch { }
      this.handleAlpineDestroy = null;
    }
    if (this.eventStreamReconnectTimer) {
      window.clearTimeout(this.eventStreamReconnectTimer);
      this.eventStreamReconnectTimer = null;
    }
    if (this.playersSaveTimer) {
      window.clearTimeout(this.playersSaveTimer);
      this.playersSaveTimer = null;
    }
    if (this.undoArmTimer) {
      window.clearTimeout(this.undoArmTimer);
      this.undoArmTimer = null;
    }
    if (this.setupSaveTimer) {
      window.clearTimeout(this.setupSaveTimer);
      this.setupSaveTimer = null;
    }
    if (this.setupSaveStateTimer) {
      window.clearTimeout(this.setupSaveStateTimer);
      this.setupSaveStateTimer = null;
    }
    // Scoring lock: beacon release runs from pagehide / teardown unless navigation is a reload
    // (see maybeBeaconReleaseControlOnUnload). Explicit release still from close() / Release button.
    this.flushPendingSetupSaves({ silent: true }).catch(() => { });
    this.disconnectEventStream();
    this.stopHeartbeatLoop();
    this.stopReplayLoop();
    if (this.handleOnline) {
      window.removeEventListener('online', this.handleOnline);
      this.handleOnline = null;
    }
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
      this.handleScroll = null;
    }
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
      this.handleResize = null;
    }
    if (this.handleKeydown) {
      window.removeEventListener('keydown', this.handleKeydown, true);
      this.handleKeydown = null;
    }
    if (this.handleFullscreenChange) {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      this.handleFullscreenChange = null;
    }
  },

  async loadFixture() {
    const res = await apiFetch(`/fixtures/${this.fixtureId}?populate=true`);
    const data = await res.json();
    if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to load fixture');
    this.fixture = data.fixture;
  },

  hydrateTeamsFromFixture() {
    const fx = this.fixture;
    if (!fx) {
      this.teams = [];
      return;
    }
    const rows = [
      { ...(fx.homeTeam || fx.home || {}), _id: fx.homeTeamId, name: fx.homeTeamName },
      { ...(fx.awayTeam || fx.away || {}), _id: fx.awayTeamId, name: fx.awayTeamName },
    ];
    this.teams = rows
      .map((row) => {
        const id = String(row?._id || row?.id || '').trim();
        const name = String(row?.name || '').trim();
        if (!id && !name) return null;
        return { ...row, _id: id || row?._id, name: name || id };
      })
      .filter(Boolean);
  },

  hydratePlayersFromFixture() {
    const seen = new Set();
    const out = [];
    const ingest = (list) => {
      for (const raw of list || []) {
        const id = String(raw?._id || raw?.id || '').trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push({
          ...raw,
          _id: id,
          fullName: String(raw.fullName || raw.nickname || raw.name || '').trim(),
          imageUrl: resolvePlayerAvatarUrl(raw),
        });
      }
    };
    for (const side of ['home', 'away']) {
      const pool = this.fixture?.[side]?.players
        || this.fixture?.[`${side}Team`]?.players
        || {};
      ingest(pool.roster);
      ingest(pool.reserves);
    }
    for (const block of this.fixture?.borrowPool || []) {
      ingest(block?.players);
    }
    if (out.length) this.allPlayers = out;
  },

  async loadTeams() {
    if (this.venueKioskFixtureHubMode()) {
      this.hydrateTeamsFromFixture();
      return;
    }
    try {
      const res = await apiFetch('/teams?limit=200&sortBy=name:asc');
      if (!res.ok) return;
      const data = await res.json();
      this.teams = data.items || data.teams || [];
    } catch (error) {
      console.error('Teams load failed', error);
    }
  },

  async loadBorrowablePlayers() {
    if (this.venueKioskFixtureHubMode()) return;
    try {
      const allItems = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await apiFetch(`/players?limit=100&page=${page}&sortBy=fullName:asc`);
        if (!res.ok) break;
        const j = await res.json();
        allItems.push(...(j.items || []));
        totalPages = Number(j.totalPages || 1) || 1;
        page += 1;
      } while (page <= totalPages);

      const allowed = new Set(['active', 'reserve', 'pool']);
      this.allPlayers = allItems
        .filter((player) => allowed.has(String(player?.status || '').toLowerCase()))
        .map((player) => ({
          ...player,
          name: String(player.fullName || player.nickname || '').trim(),
        }));
    } catch (error) {
      console.error('Players load failed', error);
    }
  },

  borrowCandidateLabel(player) {
    if (!player) return '';
    const base = String(player.name || player.fullName || player.nickname || '').trim();
    if (!base) return '';
    const st = String(player?.status || '').toLowerCase();
    if (st === 'reserve') return `${base} (reserve)`;
    if (st === 'pool') return `${base} (spare pool)`;
    return base;
  },

  borrowCandidateTeamLine(player) {
    if (!player) return 'Other team';
    if (String(player.status || '').toLowerCase() === 'pool') return 'Spare pool';
    return this.playerTeamName(player) || 'Other team';
  },

  setupRosterOptionLabel(player) {
    if (!player) return '';
    const base = String(player.fullName || player.nickname || '').trim();
    const st = String(player.status || '').toLowerCase();
    if (st === 'reserve') return `${base} (reserve)`;
    if (st === 'pool') return `${base} (spare pool)`;
    return base;
  },

  sessionEventUrl() {
    const token = encodeURIComponent(getAccessToken() || '');
    const apiBase = String(window.__API_BASE__ || '').replace(/\/+$/, '');
    const mode = this.isControllerOwner() ? 'scorer' : 'viewer';
    const path = `/api/fixture-sessions/${this.session?._id}/events?access_token=${token}&deviceId=${encodeURIComponent(this.deviceId)}&deviceLabel=${encodeURIComponent(this.deviceLabel)}&mode=${mode}`;
    return apiBase ? `${apiBase}${path}` : path;
  },

  sessionBody(extra = {}) {
    return {
      deviceId: this.deviceId,
      deviceLabel: this.deviceLabel,
      ...extra,
    };
  },

  currentController() {
    return this.session?.control || null;
  },

  /** True when another device holds a real scoring lock (ignores empty/stale control snapshots). */
  fixtureControlHeldByOtherDevice() {
    const controller = this.currentController();
    if (!controller) return false;
    const controlDeviceId = String(controller.deviceId || '').trim();
    if (!controlDeviceId) return false;
    return controlDeviceId !== String(this.deviceId || '');
  },

  isControllerOwner() {
    return String(this.currentController()?.deviceId || '') === String(this.deviceId || '');
  },

  canScore() {
    if (this.remoteVenueScoringEndedVisible?.()) return false;
    return !!this.isControllerOwner();
  },

  fixtureSessionIsFinalized() {
    const s = String(this.session?.status || '').toLowerCase();
    return ['completed', 'submitted', 'verified'].includes(s);
  },

  fixtureScoringFrozen() {
    if (this.remoteVenueScoringEndedVisible?.()) return true;
    if (this.fixtureSessionIsFinalized()) return true;
    return this.navigatorGameIsComplete(this.currentGame());
  },

  /** Server set when a game is won but Match Console has not advanced yet. */
  hasPendingNextMatch() {
    const p = this.session?.pendingAdvanceToGameIndex;
    if (p === null || p === undefined || p === '') return false;
    const n = Number(p);
    return Number.isInteger(n) && n >= 0;
  },

  pendingNextGame() {
    if (!this.hasPendingNextMatch()) return null;
    return this.session?.games?.[Number(this.session.pendingAdvanceToGameIndex)] || null;
  },

  pendingFinishedMatchWinnerLine() {
    const g = this.currentGame();
    if (!g) return 'This match is complete.';
    const w = String(g.winnerSide || '');
    if (!['home', 'away'].includes(w)) return 'This match is complete.';
    const name = w === 'home' ? (this.fixture?.homeTeamName || 'Home') : (this.fixture?.awayTeamName || 'Away');
    return `${name} won this match.`;
  },

  showMatchCompleteOverlay() {
    if (!this.scoringWindow || this.fixtureSessionIsFinalized()) return false;
    if (this.hasPendingNextMatch()) return true;
    const game = this.currentGame();
    return !!game && this.navigatorGameIsComplete(game);
  },

  canContinueToNextMatch() {
    return this.hasPendingNextMatch() && !!this.pendingNextGame();
  },

  isLastMatchJustFinished() {
    return this.showMatchCompleteOverlay() && !this.canContinueToNextMatch();
  },

  matchCompletePrimaryLabel() {
    if (this.canContinueToNextMatch()) {
      const next = this.pendingNextGame();
      return next ? `Continue to ${this.gameLabel(next)}` : 'Continue to next match';
    }
    if (this.fromVenueKiosk) return 'Back to match overview';
    return '';
  },

  matchCompleteSummaryLine() {
    if (this.canContinueToNextMatch()) {
      return 'Start the next match when ready, or undo the last score if it was entered wrong.';
    }
    if (this.fromVenueKiosk) {
      return 'This was the last match in the program.';
    }
    return 'Ask the venue tablet to finish the fixture.';
  },

  liveScoringKeypadEnabled() {
    return this.setupReady() && this.canScore() && this.hasRequiredPlayers() && !this.fixtureScoringFrozen();
  },

  /** Match console can correct throws without holding the dedicated scorer control lock. */
  consoleVisitEditSaveReady() {
    return this.setupReady() && this.hasRequiredPlayers() && !this.fixtureScoringFrozen();
  },

  canBrowseFixtureGames() {
    return this.fixtureSessionIsFinalized() || this.canScore();
  },

  /** Games list: only finished matches are disabled so history can focus on open games without claiming control first. */
  navigatorGameRowDisabled(game) {
    if (!this.assignmentSetupReady()) return true;
    return this.navigatorGameIsComplete(game);
  },

  /** Match console: show Take Control only when another device (e.g. mobile scorer) holds the lock. */
  matchConsoleRemoteScorerHasLock() {
    return (
      !this.scoringWindow
      && !!this.currentController()
      && !this.isControllerOwner()
      && !this.fixtureSessionIsFinalized()
    );
  },

  fixtureScoringFrozenMessage() {
    if (this.fixtureSessionIsFinalized()) return 'Fixture finished';
    if (this.navigatorGameIsComplete(this.currentGame())) return 'This game is finished';
    return '';
  },

  fixtureScoringFrozenDetail() {
    if (this.fixtureSessionIsFinalized()) return 'Scoring is closed. You can still switch games to review history.';
    if (this.navigatorGameIsComplete(this.currentGame())) return 'Open the current match in the Games list to enter live scores.';
    return '';
  },

  setupState() {
    return this.session?.runtime?.setup || {
      ready: false,
      homeReady: false,
      awayReady: false,
      games: [],
      scoringUnlocked: false,
      captainLaunch: { home: { acknowledged: false }, away: { acknowledged: false } },
    };
  },

  /** All singles/doubles lineup slots filled (server `runtime.setup.ready`). */
  assignmentSetupReady() {
    return !!this.setupState()?.ready;
  },

  /** Lineups complete + both captains confirmed ready (or legacy active session with throws / pre-gate live). */
  setupReady() {
    return !!this.setupState()?.scoringUnlocked;
  },

  setupBlockingMessage() {
    return 'Complete player assignment setup before scoring starts';
  },

  setupReadinessDetail() {
    if (!this.assignmentSetupReady()) return 'Assign all singles and doubles players for both teams in Fixture Setup.';
    if (!this.setupReady()) return 'Both team captains must tap Captain ready below before scoring opens.';
    if (!this.canScore()) return 'Take scoring control on this device to start the fixture.';
    if (!this.hasRequiredPlayers()) return 'Select players for the current game in the Games panel before entering scores.';
    return 'Fixture is ready to start.';
  },

  captainLaunchHomeAcknowledged() {
    return !!this.setupState()?.captainLaunch?.home?.acknowledged;
  },

  captainLaunchAwayAcknowledged() {
    return !!this.setupState()?.captainLaunch?.away?.acknowledged;
  },

  captainLaunchGateVisible() {
    if (this.fixtureSessionIsFinalized()) return false;
    return this.assignmentSetupReady() && !this.setupReady();
  },

  captainDisplayNameForSide(side) {
    const id = side === 'home' ? this.fixture?.homeCaptainPlayerId : this.fixture?.awayCaptainPlayerId;
    if (!id) return '—';
    return this.playerName(id);
  },

  canCaptainAckSide(side) {
    if (this.$store.session?.hasRole?.('admin')) return true;
    const me = String(this.$store.session?.me?._id || '');
    const cap = side === 'home'
      ? String(this.fixture?.homeCaptainPlayerId || '')
      : String(this.fixture?.awayCaptainPlayerId || '');
    return !!me && !!cap && me === cap;
  },

  async postCaptainLaunchReady(side) {
    if (!this.session?._id || !['home', 'away'].includes(side)) return;
    if (!this.assignmentSetupReady()) {
      Alpine.store('toastr').info('Finish player assignments first', 2500);
      return;
    }
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/captain-launch-ready`, {
        method: 'POST',
        body: { side },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Unable to confirm');
      this.applyRemoteSession(data.session);
      this.$nextTick?.(() => this.renderScorerQr());
      Alpine.store('toastr').success(`${side === 'home' ? 'Home' : 'Away'} captain confirmed`, 2500);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to confirm', 3000);
    } finally {
      this.saving = false;
    }
  },

  matchConsoleBrandingLogo() {
    return String(this.fixture?.leagueImageUrl || '').trim() || '/assets/darts-league-logo.png';
  },

  toggleFixtureSetupHub() {
    this.fixtureSetupHubCollapsed = !this.fixtureSetupHubCollapsed;
  },

  activeScoringControllerDisplayLine() {
    const controller = this.currentController();
    if (!controller) return '';
    const name = String(controller.userName || '').trim();
    const label = String(controller.label || '').trim();
    const did = String(controller.deviceId || '').trim();
    if (name && label) return `${name} · ${label}`;
    if (name) return name;
    if (label) return label;
    if (did) return did.length > 24 ? `${did.slice(0, 10)}…` : did;
    return 'Another device';
  },

  /** Show active controller or missing-controller hint beside Live status (after setup, before fixture finalized). */
  scoringControllerStatusPillVisible() {
    if (!this.assignmentSetupReady()) return false;
    if (this.fixtureSessionIsFinalized()) return false;
    return true;
  },

  matchConsolePageUrl() {
    return `/admin/fixtures/${this.fixtureId}`;
  },

  dedicatedScorerPageUrl() {
    return `/admin/fixtures/${this.fixtureId}/scorer`;
  },

  async openBoardViewInNewTab() {
    const flushed = await this.flushPendingSetupSaves({ silent: false });
    if (!flushed) return;
    if (!this.setupReady()) {
      Alpine.store('toastr').info(
        this.assignmentSetupReady()
          ? 'Both team captains must confirm ready before opening the board'
          : 'Complete both team setup cards before opening the board',
        2800,
      );
      return;
    }
    const url = `/admin/fixtures/${this.fixtureId}/board`;
    const w = window.open(url, '_blank');
    if (w) w.opener = null;
  },

  scorerBlockedMessage() {
    if (!this.assignmentSetupReady()) return this.setupBlockingMessage();
    if (!this.setupReady()) return 'Waiting for both team captains on the Match Console';
    if (!this.hasRequiredPlayers()) return 'Select players for this game before scoring';
    return this.isControllerOwner()
      ? ''
      : 'This scorer is locked to another device';
  },

  scorerBlockedDetail() {
    if (!this.assignmentSetupReady()) return 'Finish both team setup cards on Match Console. This scorer will unlock automatically.';
    if (!this.setupReady()) return 'Each team captain must tap Captain ready on the Match Console before scoring starts.';
    if (!this.hasRequiredPlayers()) return 'Use the Games panel to assign Home and Away players for the selected game.';
    const controller = this.currentController();
    if (!controller) return 'Open the scorer again or claim control from Match Console.';
    const name = String(controller.userName || controller.label || '').trim();
    if (name && !/^fixture-cp_/i.test(name)) return `Scoring on: ${name}`;
    return 'Another device is scoring this fixture.';
  },

  myTeamSide() {
    const myTeamId = String(this.$store.session?.me?.registeredTeamId || '');
    if (!myTeamId) return '';
    if (myTeamId === String(this.session?.homeTeamId || this.fixture?.homeTeamId || '')) return 'home';
    if (myTeamId === String(this.session?.awayTeamId || this.fixture?.awayTeamId || '')) return 'away';
    return '';
  },

  canEditSetupSide(side) {
    return this.$store.session?.hasRole?.('admin') || this.myTeamSide() === side;
  },

  fixtureSetupSessionLive() {
    const st = String(this.session?.status || '').toLowerCase();
    return !['completed', 'submitted', 'verified', 'abandoned'].includes(st);
  },

  fixtureSetupAllowEditMode() {
    const st = String(this.session?.status || '').toLowerCase();
    return !['completed', 'submitted', 'verified', 'abandoned'].includes(st);
  },

  fixtureSetupShowEditAssignments() {
    return !this.scoringWindow && this.fixtureSetupSessionLive() && this.fixtureSetupAllowEditMode();
  },

  toggleSetupAssignmentsEdit() {
    this.setupAssignmentsEditMode = !this.setupAssignmentsEditMode;
    if (!this.setupAssignmentsEditMode) {
      this.flushPendingSetupSaves({ silent: true }).catch(() => { });
    }
  },

  setupGameLocked(gameOrIndex) {
    const game = Number.isInteger(gameOrIndex)
      ? this.session?.games?.[Number(gameOrIndex)]
      : gameOrIndex;
    if (!game) return true;
    if (this.navigatorGameIsComplete(game)) return true;
    if (this.navigatorGameHasStarted(game)) return true;
    return false;
  },

  canEditSetupGameSide(gameOrIndex, side) {
    if (!this.canEditSetupSide(side)) return false;
    if (this.fixtureSetupSessionLive() && !this.setupAssignmentsEditMode) return false;
    if (this.setupGameLocked(gameOrIndex)) return false;
    return true;
  },

  setupSideHelpText(gameOrIndex, side) {
    if (this.fixtureSetupSessionLive() && !this.setupAssignmentsEditMode) {
      return 'Locked while match is live';
    }
    if (this.setupGameLocked(gameOrIndex)) {
      const game = Number.isInteger(gameOrIndex)
        ? this.session?.games?.[Number(gameOrIndex)]
        : gameOrIndex;
      if (game && this.navigatorGameIsComplete(game)) return 'Game complete';
      return 'Locked after this match started';
    }
    return this.canEditSetupSide(side) ? 'You can edit this side' : 'Locked to that team';
  },

  setupGames() {
    return (this.session?.games || []).filter((game) => ['singles', 'doubles'].includes(String(game?.type || '')));
  },

  setupTabs() {
    return [
      { key: 'singles', label: 'Singles' },
      { key: 'doubles', label: 'Doubles' },
    ];
  },

  setupEditableSides() {
    return ['home', 'away'].filter((side) => this.canEditSetupSide(side));
  },

  setupEditableSidesForGames(games = []) {
    return ['home', 'away'].filter((side) =>
      this.canEditSetupSide(side) && games.some((game) => !this.setupGameLocked(this.setupGameIndex(game))),
    );
  },

  canRandomizeSetupTab() {
    if (this.fixtureSetupSessionLive() && !this.setupAssignmentsEditMode) return false;
    const games = this.setupTabGames().filter((game) => !this.setupGameLocked(this.setupGameIndex(game)));
    if (!games.length) return false;
    return this.setupEditableSidesForGames(games).some((side) => this.roster(side).length > 0);
  },

  setupAutoFillLabel() {
    return `Auto Fill ${String(this.setupTab || 'singles').replace(/^./, (c) => c.toUpperCase())}`;
  },

  setSetupSaveState(state) {
    this.setupSaveState = state;
    if (this.setupSaveStateTimer) {
      window.clearTimeout(this.setupSaveStateTimer);
      this.setupSaveStateTimer = null;
    }
    if (state === 'saved') {
      this.setupSaveStateTimer = window.setTimeout(() => {
        this.setupSaveState = 'idle';
        this.setupSaveStateTimer = null;
      }, 1800);
    }
  },

  setupTabGames() {
    return this.setupGames().filter((game) => String(game?.type || '') === String(this.setupTab || 'singles'));
  },

  setupGameIndex(game) {
    const gameId = String(game?._id || '');
    if (gameId) {
      const byId = (this.session?.games || []).findIndex((item) => String(item?._id || '') === gameId);
      if (byId >= 0) return byId;
    }
    const order = Number(game?.order || 0);
    const type = String(game?.type || '');
    return (this.session?.games || []).findIndex((item) => Number(item?.order || 0) === order && String(item?.type || '') === type);
  },

  setSetupTab(tab) {
    this.flushPendingSetupSaves({ silent: true }).finally(() => {
      this.setupTab = ['singles', 'doubles'].includes(String(tab || '')) ? String(tab) : 'singles';
      window.requestAnimationFrame(() => {
        try {
          const top = (this.$refs?.setupTabsPanel?.getBoundingClientRect?.().top || 0) + window.scrollY - 72;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        } catch { }
      });
    });
  },

  setupExpectedPlayers(game) {
    const type = String(game?.type || 'singles');
    return type === 'doubles' ? 2 : 1;
  },

  setupLabel(game) {
    const order = Number(game?.order || 0);
    return `${String(game?.type || 'singles') === 'doubles' ? 'Doubles' : 'Singles'} ${order <= 4 ? order : order - 4}`;
  },

  setupPlayerIds(game, side) {
    const expected = this.setupExpectedPlayers(game);
    const ids = ((side === 'home' ? game?.homePlayers : game?.awayPlayers) || []).map((id) => String(id || ''));
    while (ids.length < expected) ids.push('');
    return ids.slice(0, expected);
  },

  setupPlayerMeta(game, side) {
    const expected = this.setupExpectedPlayers(game);
    const ids = this.setupPlayerIds(game, side);
    const metaKey = side === 'home' ? 'homePlayerMeta' : 'awayPlayerMeta';
    const meta = Array.isArray(game?.[metaKey]) ? game[metaKey] : [];
    const entries = ids.map((playerId, index) => {
      const raw = meta[index] || {};
      const samePlayer = String(raw?.playerId || '') === String(playerId || '');
      return {
        playerId,
        borrowed: samePlayer ? !!raw?.borrowed : false,
        borrowedFromTeamId: samePlayer ? String(raw?.borrowedFromTeamId || '') : '',
        borrowedFromTeamName: samePlayer ? String(raw?.borrowedFromTeamName || '') : '',
      };
    });
    while (entries.length < expected) {
      entries.push({ playerId: '', borrowed: false, borrowedFromTeamId: '', borrowedFromTeamName: '' });
    }
    return entries.slice(0, expected);
  },

  setupPlayerEntry(game, side, slotIndex) {
    return this.setupPlayerMeta(game, side)[slotIndex] || { playerId: '', borrowed: false, borrowedFromTeamId: '', borrowedFromTeamName: '' };
  },

  beerPlayerIds(side) {
    const game = (this.session?.games || []).find((item) => String(item?.type || '') === 'beer');
    const ids = ((side === 'home' ? game?.homePlayers : game?.awayPlayers) || []).map((id) => String(id || ''));
    return ids.filter(Boolean).slice(0, 4);
  },

  setupSlotLabel(game, side, slotIndex) {
    const sideLabel = side === 'home' ? 'Home' : 'Away';
    if (this.setupExpectedPlayers(game) === 1) return `${sideLabel} player`;
    return `${sideLabel} player ${slotIndex + 1}`;
  },

  setupSideReady(game, side) {
    const expected = this.setupExpectedPlayers(game);
    return this.setupPlayerIds(game, side).filter(Boolean).length >= expected;
  },

  shuffledIds(items = []) {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  },

  randomSetupAssignments(side, games) {
    const rosterIds = this.shuffledIds(
      this.roster(side)
        .filter((player) => String(player?.status || '').toLowerCase() !== 'reserve')
        .map((player) => String(player?._id || ''))
        .filter(Boolean),
    );
    if (!rosterIds.length) return [];
    const assignments = [];
    let cursor = 0;
    for (const game of games) {
      const expected = this.setupExpectedPlayers(game);
      const selected = [];
      const used = new Set();
      let attempts = 0;
      while (selected.length < expected && attempts < Math.max(rosterIds.length * 3, expected * 3)) {
        const playerId = rosterIds[cursor % rosterIds.length];
        cursor += 1;
        attempts += 1;
        if (!playerId || used.has(playerId)) continue;
        used.add(playerId);
        selected.push(playerId);
      }
      if (selected.length < expected) {
        for (const playerId of rosterIds) {
          if (selected.length >= expected) break;
          if (!playerId || used.has(playerId)) continue;
          used.add(playerId);
          selected.push(playerId);
        }
      }
      assignments.push(selected);
    }
    return assignments;
  },

  randomizeSetupTab() {
    if (this.fixtureSetupSessionLive() && !this.setupAssignmentsEditMode) return;
    const games = this.setupTabGames().filter((game) => !this.setupGameLocked(this.setupGameIndex(game)));
    if (!games.length) return;
    if (this.setupSaveTimer) {
      window.clearTimeout(this.setupSaveTimer);
      this.setupSaveTimer = null;
    }
    this.pendingSetupSaves = {};
    const bulkUpdates = [];
    let changed = 0;
    for (const side of this.setupEditableSidesForGames(games)) {
      const assignments = this.randomSetupAssignments(side, games);
      if (!assignments.length) continue;
      games.forEach((game, index) => {
        const gameIndex = this.setupGameIndex(game);
        if (gameIndex < 0) return;
        const finalPlayers = assignments[index] || [];
        const playerMeta = this.setupPlayerMetaPayload(game, side, finalPlayers);
        this.applyLocalSetupPlayers(gameIndex, side, finalPlayers, playerMeta);
        bulkUpdates.push({
          gameIndex,
          side,
          players: [...finalPlayers],
          playerMeta: Array.isArray(playerMeta) ? [...playerMeta] : [],
        });
        changed += 1;
      });
    }
    if (!changed) {
      Alpine.store('toastr').error('No players available to auto fill', 2500);
      return;
    }
    this.saveSetupBulk(bulkUpdates).then((ok) => {
      if (ok) {
        Alpine.store('toastr').success(`${String(this.setupTab || 'singles').replace(/^./, (c) => c.toUpperCase())} auto-filled`, 2000);
      }
    });
  },

  setupGameReady(game) {
    return this.setupSideReady(game, 'home') && this.setupSideReady(game, 'away');
  },

  setupSelectedSet(game, side, excludeIndex = -1) {
    return new Set(
      this.setupPlayerIds(game, side)
        .filter((playerId, index) => index !== excludeIndex && !!playerId)
        .map(String),
    );
  },

  setupOptionDisabled(game, side, slotIndex, playerId) {
    if (!playerId) return false;
    return this.setupSelectedSet(game, side, slotIndex).has(String(playerId));
  },

  isSetupBorrowedEntry(game, side, slotIndex) {
    return !!this.setupPlayerEntry(game, side, slotIndex)?.borrowed;
  },

  setupBorrowedEntryName(game, side, slotIndex) {
    const entry = this.setupPlayerEntry(game, side, slotIndex);
    if (!entry?.playerId) return 'Borrowed player';
    return this.playerName(entry.playerId) || 'Borrowed player';
  },

  setupBorrowSummary(game, side, slotIndex) {
    const entry = this.setupPlayerEntry(game, side, slotIndex);
    if (!entry?.borrowed) return '';
    return entry.borrowedFromTeamName ? `Borrowed from ${entry.borrowedFromTeamName}` : 'Borrowed player';
  },

  setupPlayerMetaPayload(game, side, players, overrides = {}) {
    return this.setupPlayerMeta(game, side)
      .slice(0, players.length)
      .map((entry, index) => {
        const playerId = String(players[index] || '');
        const override = overrides[index] || {};
        if (!playerId) {
          return {
            playerId: '',
            borrowed: false,
            borrowedFromTeamId: '',
            borrowedFromTeamName: '',
          };
        }
        return {
          playerId,
          borrowed: !!override.borrowed || !!(entry?.borrowed && String(entry?.playerId || '') === playerId),
          borrowedFromTeamId: String(override.borrowedFromTeamId || ((entry?.borrowed && String(entry?.playerId || '') === playerId) ? entry?.borrowedFromTeamId : '') || ''),
          borrowedFromTeamName: String(override.borrowedFromTeamName || ((entry?.borrowed && String(entry?.playerId || '') === playerId) ? entry?.borrowedFromTeamName : '') || ''),
        };
      });
  },

  async saveSetupPlayers(gameIndex, side, players, playerMeta = []) {
    this.setSetupSaveState('saving');
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/${gameIndex}/setup`, {
        method: 'PATCH',
        body: this.sessionBody({ side, players, playerMeta }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to save fixture setup');
      this.applyRemoteSession(data.session);
      this.setSetupSaveState('saved');
      return true;
    } catch (e) {
      this.setSetupSaveState('idle');
      Alpine.store('toastr').error(e.message || 'Unable to save fixture setup', 3000);
      return false;
    }
  },

  async saveSetupBulk(updates = []) {
    const payload = Array.isArray(updates) ? updates : [];
    if (!payload.length) return true;
    this.setSetupSaveState('saving');
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/setup-bulk`, {
        method: 'PATCH',
        body: this.sessionBody({ updates: payload }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to save fixture setup');
      this.applyRemoteSession(data.session);
      this.setSetupSaveState('saved');
      return true;
    } catch (e) {
      this.setSetupSaveState('idle');
      Alpine.store('toastr').error(e.message || 'Unable to save fixture setup', 3000);
      return false;
    }
  },

  applyLocalSetupPlayers(gameIndex, side, players, playerMeta = []) {
    const game = this.session?.games?.[gameIndex];
    if (!game) return;
    const expected = this.setupExpectedPlayers(game);
    const normalizedPlayers = [...players].map((id) => String(id || '')).filter(Boolean).slice(0, expected);
    const playerKey = side === 'home' ? 'homePlayers' : 'awayPlayers';
    const metaKey = side === 'home' ? 'homePlayerMeta' : 'awayPlayerMeta';
    game[playerKey] = normalizedPlayers;
    game[metaKey] = Array.isArray(playerMeta) ? playerMeta : [];
    if (this.session?.currentGameIndex === gameIndex) this.hydratePlayerSelections();
  },

  scheduleSetupSave(gameIndex, side, players, playerMeta = []) {
    const key = `${gameIndex}:${side}`;
    this.pendingSetupSaves[key] = {
      gameIndex,
      side,
      players: [...players],
      playerMeta: Array.isArray(playerMeta) ? [...playerMeta] : [],
    };
    if (this.setupSaveTimer) window.clearTimeout(this.setupSaveTimer);
    this.setSetupSaveState('saving');
    this.setupSaveTimer = window.setTimeout(() => {
      this.setupSaveTimer = null;
      this.flushPendingSetupSaves({ silent: true }).catch(() => { });
    }, 800);
  },

  async flushPendingSetupSaves({ silent = false } = {}) {
    if (this.setupSaveTimer) {
      window.clearTimeout(this.setupSaveTimer);
      this.setupSaveTimer = null;
    }
    const queue = Object.values(this.pendingSetupSaves || {});
    this.pendingSetupSaves = {};
    if (!queue.length) return true;
    for (const item of queue) {
      const ok = await this.saveSetupPlayers(item.gameIndex, item.side, item.players, item.playerMeta);
      if (!ok) {
        this.pendingSetupSaves[`${item.gameIndex}:${item.side}`] = item;
        if (!silent) Alpine.store('toastr').error('Unable to save fixture setup', 3000);
        return false;
      }
    }
    return true;
  },

  async updateSetupPlayer(gameIndex, side, slotIndex, value) {
    if (!this.canEditSetupGameSide(gameIndex, side)) return;
    const game = this.session?.games?.[gameIndex];
    if (!game) return;
    const players = this.setupPlayerIds(game, side);
    players[slotIndex] = String(value || '');
    const deduped = [];
    for (const playerId of players) {
      if (!playerId || deduped.includes(playerId)) continue;
      deduped.push(playerId);
    }
    while (deduped.length < this.setupExpectedPlayers(game)) deduped.push('');
    const finalPlayers = deduped.filter(Boolean);
    const playerMeta = this.setupPlayerMetaPayload(game, side, finalPlayers);
    this.applyLocalSetupPlayers(gameIndex, side, finalPlayers, playerMeta);
    this.scheduleSetupSave(gameIndex, side, finalPlayers, playerMeta);
  },

  hasRequiredPlayers() {
    if (!this.assignmentSetupReady()) return false;
    const expected = this.expectedPlayers();
    const homeCount = this.selectedHomePlayers.filter(Boolean).length;
    const awayCount = this.selectedAwayPlayers.filter(Boolean).length;
    return homeCount >= expected && awayCount >= expected;
  },

  syncLocalEnvelope(remoteSession = null) {
    if (!this.localSession) return;
    const pending = Array.isArray(this.localSession.offline?.queue) ? this.localSession.offline.queue : [];
    this.localSession.revision = Math.max(
      Number(this.localSession.revision || 0),
      Number(remoteSession?.revision || 0),
    );
    this.localSession.updatedAt = new Date().toISOString();
    this.localSession.offline = {
      ...(this.localSession.offline || {}),
      queue: pending,
      dirty: pending.length > 0,
      lastSyncedAt: pending.length ? this.localSession.offline?.lastSyncedAt || null : new Date().toISOString(),
    };
    this.localSession.controller = {
      ...(this.localSession.controller || {}),
      pendingEvents: pending.length,
      online: navigator.onLine !== false,
    };
    saveFixtureLiveSession(window.localStorage, this.localSession);
  },

  applyRemoteSession(session) {
    if (!session) return;

    const incomingRevision = Number(session?.revision || 0);
    const currentRevision = Number(this.session?.revision || 0);

    // Ignore older snapshots/events
    if (currentRevision > 0 && incomingRevision > 0 && incomingRevision < currentRevision) {
      return;
    }

    const previousGameIndex = Number(this.session?.currentGameIndex ?? -1);
    const previousGameStatus = String(this.currentGame()?.status || '');
    const previousSessionStatus = String(this.session?.status || '');
    const previousRevision = currentRevision;
    const previousHomePoints = Number(this.session?.score?.homePoints || 0);
    const previousAwayPoints = Number(this.session?.score?.awayPoints || 0);

    this.session = session;
    this.syncFixtureKioskAuthFromSession(session);
    const sessionStatus = String(session?.status || '').toLowerCase();
    if (this.isRemoteVenueMobileFixtureScorer() && sessionStatus === 'abandoned') {
      this.openRemoteVenueScoringEnded('abandoned');
    }
    if (!this.fixtureSetupAllowEditMode() || !this.fixtureSetupSessionLive()) {
      this.setupAssignmentsEditMode = false;
    }
    this.eventStreamHealthy = true;
    this.syncLocalEnvelope(session);
    this.recoveryNotice = this.localSession?.offline?.queue?.length
      ? 'Offline changes pending sync'
      : '';
    this.hydratePlayerSelections();

    const nextGameIndex = Number(this.session?.currentGameIndex ?? -1);
    const nextGameStatus = String(this.currentGame()?.status || '');
    const nextRevision = Number(this.session?.revision || 0);
    const nextHomePoints = Number(this.session?.score?.homePoints || 0);
    const nextAwayPoints = Number(this.session?.score?.awayPoints || 0);

    if (previousGameIndex >= 0 && nextGameIndex !== previousGameIndex) {
      this.scoreInput = '';
      this.checkoutDarts = '';
      this.editingVisitIndex = -1;
      this.quickScoresExpanded = false;
      if (previousGameStatus === 'live' && nextGameStatus === 'live') {
        Alpine.store('toastr').success(`Moved to ${this.gameLabel(this.currentGame())}`, 2200);
      }
      try {
        this.$refs?.navigatorPanel?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      } catch { }
    }

    if (String(this.session?.status || '') === 'completed' && previousSessionStatus !== 'completed') {
      this.scoreInput = '';
      this.checkoutDarts = '';
      this.editingVisitIndex = -1;
      this.quickScoresExpanded = false;
      this.refreshCompletionScoresheetDraft();
    }

    if (this.editingVisitIndex >= ((this.currentLeg()?.visits || []).length)) {
      this.editingVisitIndex = -1;
    }

    this.$nextTick?.(() => this.renderScorerQr());
  },

  // applyRemoteSession(session) {
  //   if (!session) return;
  //   const previousGameIndex = Number(this.session?.currentGameIndex ?? -1);
  //   const previousGameStatus = String(this.currentGame()?.status || '');
  //   const previousSessionStatus = String(this.session?.status || '');
  //   const previousRevision = Number(this.session?.revision || 0);
  //   const previousHomePoints = Number(this.session?.score?.homePoints || 0);
  //   const previousAwayPoints = Number(this.session?.score?.awayPoints || 0);
  //   this.session = session;
  //   this.eventStreamHealthy = true;
  //   this.syncLocalEnvelope(session);
  //   this.recoveryNotice = this.localSession?.offline?.queue?.length
  //     ? 'Offline changes pending sync'
  //     : '';
  //   this.hydratePlayerSelections();
  //   const nextGameIndex = Number(this.session?.currentGameIndex ?? -1);
  //   const nextGameStatus = String(this.currentGame()?.status || '');
  //   const nextRevision = Number(this.session?.revision || 0);
  //   const nextHomePoints = Number(this.session?.score?.homePoints || 0);
  //   const nextAwayPoints = Number(this.session?.score?.awayPoints || 0);
  //   if (previousGameIndex >= 0 && nextGameIndex !== previousGameIndex) {
  //     this.scoreInput = '';
  //     this.checkoutDarts = '';
  //     this.editingVisitIndex = -1;
  //     this.quickScoresExpanded = false;
  //     if (previousGameStatus === 'live' && nextGameStatus === 'live') {
  //       Alpine.store('toastr').success(`Moved to ${this.gameLabel(this.currentGame())}`, 2200);
  //     }
  //     try {
  //       this.$refs?.navigatorPanel?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  //     } catch { }
  //   }
  //   if (String(this.session?.status || '') === 'completed' && previousSessionStatus !== 'completed') {
  //     this.scoreInput = '';
  //     this.checkoutDarts = '';
  //     this.editingVisitIndex = -1;
  //     this.quickScoresExpanded = false;
  //     this.refreshCompletionScoresheetDraft();
  //   }
  //   if (this.editingVisitIndex >= ((this.currentLeg()?.visits || []).length)) this.editingVisitIndex = -1;
  // },

  ensureEventStream() {
    if (!this.session?._id || !this.session?.runtime?.hasEventStream || !window.EventSource) return;
    if (this.eventSource) return;
    try {
      this.eventSource = new EventSource(this.sessionEventUrl());
      const handler = (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          this.eventStreamHealthy = true;
          if (payload?.session) this.applyRemoteSession(payload.session);
        } catch {
          /* ignore malformed SSE payloads */
        }
      };
      for (const evt of [
        'session:update',
        'session:created',
        'session:control',
        'session:lineup',
        'session:current-game',
        'session:players',
        'session:throw',
        'session:visit',
        'session:undo',
        'session:leg',
        'session:game',
        'session:scoresheet',
        'session:submitted',
        'session:verified',
        'session:heartbeat',
        'session:presence',
        'session:live',
      ]) {
        this.eventSource.addEventListener(evt, handler);
      }
      this.eventSource.onopen = () => {
        this.eventStreamHealthy = true;
      };
      this.eventSource.onerror = () => {
        this.eventStreamHealthy = false;
        const es = this.eventSource;
        if (es) {
          try {
            es.close();
          } catch { }
          this.eventSource = null;
        }
        if (!this.session?._id || this.loading) return;
        if (this.eventStreamReconnectTimer) return;
        this.eventStreamReconnectTimer = window.setTimeout(() => {
          this.eventStreamReconnectTimer = null;
          if (this.session?._id && !this.loading) this.ensureEventStream();
        }, 1500);
      };
    } catch {
      this.eventStreamHealthy = false;
    }
  },

  disconnectEventStream() {
    if (this.eventStreamReconnectTimer) {
      window.clearTimeout(this.eventStreamReconnectTimer);
      this.eventStreamReconnectTimer = null;
    }
    if (this.eventSource) {
      try {
        this.eventSource.close();
      } catch { }
      this.eventSource = null;
    }
    this.eventStreamHealthy = false;
  },

  async sendSessionHeartbeat() {
    if (!this.session?._id) return;
    try {
      await apiFetch(`/fixture-sessions/${this.session._id}/heartbeat`, {
        method: 'POST',
        body: this.sessionBody({ mode: this.isControllerOwner() ? 'scorer' : 'viewer' }),
      });
    } catch {
      this.eventStreamHealthy = false;
    }
  },

  ensureHeartbeatLoop() {
    if (this.heartbeatTimer || !this.session?._id) return;
    void this.sendSessionHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      void this.sendSessionHeartbeat();
    }, 10_000);
  },

  stopHeartbeatLoop() {
    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  },

  ensureReplayLoop() {
    if (this.replayTimer) return;
    this.replayTimer = window.setInterval(() => {
      this.flushPendingEvents();
    }, 3_000);
  },

  stopReplayLoop() {
    if (this.replayTimer) {
      window.clearInterval(this.replayTimer);
      this.replayTimer = null;
    }
  },

  async flushPendingEvents() {
    if (this.replayBusy || this.saving || !this.session?._id || !this.localSession?.offline?.queue?.length) return;
    this.replayBusy = true;
    try {
      while (this.localSession?.offline?.queue?.length) {
        const nextEvent = this.localSession.offline.queue[0];
        let res = null;
        if (nextEvent?.type === 'visit.recorded') {
          res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/throw`, {
            method: 'POST',
            body: this.sessionBody({
              side: nextEvent.payload?.side,
              playerId: nextEvent.payload?.playerId,
              score: nextEvent.payload?.score,
              darts: nextEvent.payload?.darts || 3,
              checkout: !!nextEvent.payload?.checkout,
            }),
          });
        } else if (nextEvent?.type === 'visit.undone') {
          res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/undo`, { method: 'POST', body: this.sessionBody() });
        } else {
          this.localSession = acknowledgeFixtureLiveEvents(this.localSession, nextEvent?.clientSeq || 0, this.session?.revision || 0);
          saveFixtureLiveSession(window.localStorage, this.localSession);
          continue;
        }

        const data = await res.json();
        if (!res.ok || !data?.ok) break;
        this.localSession = acknowledgeFixtureLiveEvents(this.localSession, nextEvent.clientSeq, data.session?.revision || null);
        this.applyRemoteSession(data.session);
      }
    } catch {
      // Keep queued events local until the next replay attempt.
    } finally {
      this.recoveryNotice = this.localSession?.offline?.queue?.length
        ? 'Offline changes pending sync'
        : '';
      this.replayBusy = false;
    }
  },

  isMobileFixtureQrScorer() {
    return !!(this.scoringWindow && this.embedMode && !this.fromVenueKiosk);
  },

  fixtureKioskAccountId() {
    return String(this.session?.accountId || this.fixture?.accountId || '').trim();
  },

  fixtureKioskRequestHeaders() {
    const accountId = this.fixtureKioskAccountId();
    return {
      'X-Kiosk-Device-Id': String(this.deviceId || ''),
      'X-Kiosk-Device-Label': String(this.deviceLabel || 'Mobile scorer'),
      ...(accountId ? { 'X-Kiosk-Account-Id': accountId } : {}),
    };
  },

  syncFixtureKioskAuthFromSession(session) {
    if (!session || !this.isMobileFixtureQrScorer()) return;
    const token = String(
      session.kioskToken || session.control?.token || '',
    ).trim();
    if (token) persistFixtureKioskToken(token);
  },

  async ensureMobileFixtureKioskAuth() {
    if (!this.isMobileFixtureQrScorer()) return;
    const fromUrl = readFixtureKioskTokenFromUrl();
    if (fromUrl) persistFixtureKioskToken(fromUrl);
    if (getAccessToken()) return;

    const existing = String(this.session?.kioskToken || '').trim();
    if (existing) {
      persistFixtureKioskToken(existing);
      return;
    }

    const res = await apiFetch(`/fixtures/${this.fixtureId}/start-session`, {
      method: 'POST',
      skipLogoutOn401: true,
      body: {
        deviceId: this.deviceId,
        deviceLabel: this.deviceLabel,
        ...(this.fixtureKioskAccountId() ? { accountId: this.fixtureKioskAccountId() } : {}),
      },
      headers: this.fixtureKioskRequestHeaders(),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.ok) {
      throw new Error(
        this.fixtureThrowErrorMessage(data?.message || data?.error)
          || data?.message
          || 'Could not authorize mobile scoring for this fixture.',
      );
    }
    if (data?.session) this.applyRemoteSession(data.session);
    const token = String(data?.kioskToken || data?.session?.kioskToken || '').trim();
    if (token) persistFixtureKioskToken(token);
    if (!getAccessToken()) {
      throw new Error('Mobile scorer could not join this fixture session. Rescan the QR from the venue tablet.');
    }
  },

  async startOrResumeSession() {
    const cachedLocal = loadFixtureLiveSession(window.localStorage, this.fixtureId, this.deviceId);
    const freshLocal = createFixtureLiveSession({
      fixtureId: this.fixtureId,
      deviceId: this.deviceId,
      league: this.fixture?.league || 'PDL',
      season: this.fixture?.season || '',
      home: { teamId: this.fixture?.homeTeamId, teamName: this.fixture?.homeTeamName },
      away: { teamId: this.fixture?.awayTeamId, teamName: this.fixture?.awayTeamName },
    });
    this.localSession = cachedLocal?.controller && cachedLocal?.offline
      ? {
        ...freshLocal,
        ...cachedLocal,
        controller: { ...freshLocal.controller, ...(cachedLocal.controller || {}) },
        offline: { ...freshLocal.offline, ...(cachedLocal.offline || {}) },
      }
      : freshLocal;

    if (this.venueKioskFixtureHubMode()) {
      const res = await apiFetch(`/fixtures/${this.fixtureId}/session`, { skipLogoutOn401: true });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to load fixture session');
      this.applyRemoteSession(data.session);
      this.ensureEventStream();
      return;
    }

    if (this.isMobileFixtureQrScorer()) {
      const res = await apiFetch(`/fixtures/${this.fixtureId}/session`, { skipLogoutOn401: true });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to load fixture session');
      this.applyRemoteSession(data.session);
      await this.ensureMobileFixtureKioskAuth();
      if (!this.fixtureSessionIsFinalized() && !this.currentController()) {
        await this.ensureControllerClaim(false);
      }
      this.ensureEventStream();
      this.ensureHeartbeatLoop();
      this.ensureReplayLoop();
      this.flushPendingEvents();
      return;
    }

    const res = await apiFetch(`/fixtures/${this.fixtureId}/start-session`, { method: 'POST', body: {} });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to start fixture session');
    this.applyRemoteSession(data.session);
    if (this.scoringWindow && !this.fixtureSessionIsFinalized() && !this.currentController()) {
      await this.ensureControllerClaim(false);
    }
    this.ensureEventStream();
    this.ensureHeartbeatLoop();
    this.ensureReplayLoop();
    this.flushPendingEvents();
  },

  async ensureControllerClaim(force = false) {
    if (!this.session?._id) return false;
    if (this.isControllerOwner()) return true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/control/claim`, {
        method: 'POST',
        body: this.sessionBody({ force }),
        headers: this.isMobileFixtureQrScorer() ? this.fixtureKioskRequestHeaders() : undefined,
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        if (data?.session) this.applyRemoteSession(data.session);
        return false;
      }
      this.applyRemoteSession(data.session);
      return this.isControllerOwner();
    } catch {
      return false;
    }
  },

  async releaseControllerClaim(opts = {}) {
    const openMatchConsoleInNewTab = !!opts.openMatchConsoleInNewTab;
    if (!this.session?._id || !this.isControllerOwner()) return;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/control/release`, {
        method: 'POST',
        body: this.sessionBody(),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        this.applyRemoteSession(data.session);
        this.disconnectEventStream();
        this.ensureEventStream();
        if (openMatchConsoleInNewTab && this.scoringWindow) {
          const w = window.open(this.matchConsolePageUrl(), '_blank', 'noopener,noreferrer');
          if (w) w.opener = null;
          Alpine.store('toastr').info('Match Console opened in a new tab', 2200);
        }
      }
    } catch { }
  },

  async takeControl() {
    /** Pre-open a tab synchronously (Match Console only) so async claim does not trip the popup blocker. */
    let scorerTab = null;
    if (!this.scoringWindow) {
      scorerTab = window.open('about:blank', '_blank');
    }
    this.saving = true;
    let claimOk = false;
    try {
      const ok = await this.ensureControllerClaim(true);
      if (!ok) {
        try {
          scorerTab?.close();
        } catch { }
        throw new Error('Unable to take fixture scoring control');
      }
      claimOk = true;
      this.disconnectEventStream();
      this.ensureEventStream();
      const url = this.dedicatedScorerPageUrl();
      if (!this.scoringWindow) {
        if (scorerTab) {
          try {
            scorerTab.location.href = url;
            scorerTab.opener = null;
          } catch {
            try {
              scorerTab.close();
            } catch { }
            const w = window.open(url, '_blank', 'noopener,noreferrer');
            if (w) w.opener = null;
          }
        } else {
          const w = window.open(url, '_blank', 'noopener,noreferrer');
          if (w) w.opener = null;
        }
        Alpine.store('toastr')?.success?.('Scoring enabled — Score Controller opened in a new tab', 2800);
      } else {
        Alpine.store('toastr')?.success?.('Scoring control moved to this device', 2500);
      }
    } catch (e) {
      if (!claimOk) {
        try {
          scorerTab?.close();
        } catch { }
      }
      Alpine.store('toastr')?.error?.(e.message || 'Unable to take scoring control', 3000);
    } finally {
      this.saving = false;
    }
  },

  hydratePlayerSelections() {
    const game = this.currentGame();
    this.selectedHomePlayers = Array.isArray(game?.homePlayers) ? game.homePlayers.map(String) : [];
    this.selectedAwayPlayers = Array.isArray(game?.awayPlayers) ? game.awayPlayers.map(String) : [];
    this.activeThrower.home = this.defaultThrowerFor('home');
    this.activeThrower.away = this.defaultThrowerFor('away');
  },

  currentGame() {
    return this.session?.currentGame || this.session?.games?.[Number(this.session?.currentGameIndex || 0)] || null;
  },

  currentLeg() {
    const game = this.currentGame();
    return game?.legs?.[game.legs.length - 1] || null;
  },

  roster(side) {
    const pool = side === 'home' ? this.fixture?.home?.players : this.fixture?.away?.players;
    const embedded = [
      ...(pool?.roster || []),
      ...(pool?.reserves || []),
    ].filter(Boolean);
    if (embedded.length) return embedded;
    const teamId = String(side === 'home'
      ? (this.session?.homeTeamId || this.fixture?.homeTeamId || '')
      : (this.session?.awayTeamId || this.fixture?.awayTeamId || ''));
    return (this.allPlayers || [])
      .filter((player) => String(player?.registeredTeamId || '') === teamId)
      .sort((a, b) => String(a?.fullName || a?.nickname || '').localeCompare(String(b?.fullName || b?.nickname || '')));
  },

  expectedPlayers() {
    const type = String(this.currentGame()?.type || 'singles');
    if (type === 'doubles') return 2;
    if (type === 'beer') return 4;
    return 1;
  },

  defaultThrowerFor(side) {
    const players = side === 'home' ? this.selectedHomePlayers : this.selectedAwayPlayers;
    return String(players?.[0] || '');
  },

  availableThrowers(side) {
    const ids = side === 'home' ? this.selectedHomePlayers : this.selectedAwayPlayers;
    return ids.filter(Boolean).map((playerId) => ({
      playerId: String(playerId),
      label: this.playerName(playerId),
    }));
  },

  setActiveThrower(side, playerId) {
    this.activeThrower[side] = String(playerId || '');
  },

  currentTurnSide() {
    return String(this.currentLeg()?.currentTurn || 'away');
  },

  isCurrentTurn(side) {
    return String(side || '') === this.currentTurnSide();
  },

  currentThrowerLabel() {
    const side = this.currentTurnSide();
    const playerId = String(this.activeThrower[side] || this.defaultThrowerFor(side) || '');
    return playerId ? this.playerName(playerId) : this.teamName(side);
  },

  currentTurnRemaining() {
    return this.remainingForSide(this.currentTurnSide());
  },

  startingScoreForCurrentGame() {
    const game = this.currentGame();
    const fmt = String(game?.format || '501').replace(/[^\d]/g, '');
    const n = Number(fmt);
    return Number.isFinite(n) && n > 0 ? n : 501;
  },

  sessionCheckoutRule() {
    const game = this.currentGame();
    const perGame = String(game?.checkout || '').trim();
    if (perGame) return perGame;
    return String(
      this.session?.defaultCheckout
      || this.session?.leagueRuleSnapshot?.defaultCheckout
      || 'double-out',
    );
  },

  currentGameMaxDartsPerLeg() {
    const raw = this.currentGame()?.maxDartsPerLeg;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  },

  /** Fixture house cap uses visit count per leg, not total darts thrown. */
  wouldExceedLegDartCapOnSubmitNative() {
    const cap = this.currentGameMaxDartsPerLeg();
    if (!cap) return false;
    if (this.editingVisitIndex >= 0) return false;
    if (!this.scoreOutcome().valid) return false;
    return this.currentLegDartCount() + 1 > cap;
  },

  maxThrownLimitNative() {
    return this.currentGameMaxDartsPerLeg();
  },

  currentLegDartCount() {
    const leg = this.currentLeg();
    const legNum = Number(leg?.number || leg?.legNumber || 1);
    const visits = this.currentGame()?.visits || [];
    return visits.filter((v) => Number(v.legNumber || 1) === legNum).length;
  },

  isMaxDartsPerLegReached() {
    const cap = this.currentGameMaxDartsPerLeg();
    if (!cap) return false;
    return this.currentLegDartCount() >= cap;
  },

  sessionCheckoutRuleLabel() {
    const r = this.sessionCheckoutRule();
    if (r === 'straight-out') return 'Straight out';
    if (r === 'master-out') return 'Master out';
    return 'Double out';
  },

  scoreOutcome() {
    const raw = String(this.scoreInput ?? '').trim();
    if (!raw) {
      return { valid: false, checkout: false, busted: false, remaining: this.currentTurnRemaining() };
    }
    const score = Number(raw);
    if (!Number.isFinite(score) || score < 0) {
      return { valid: false, checkout: false, busted: false, remaining: this.currentTurnRemaining() };
    }
    const remaining = this.currentTurnRemaining();
    const nextRemaining = Math.max(0, remaining - score);
    const rule = this.sessionCheckoutRule();
    let busted;
    if (rule === 'straight-out') {
      busted = score > remaining;
    } else {
      busted = score > remaining || nextRemaining === 1;
    }
    const checkout = !busted && nextRemaining === 0;
    return { valid: true, checkout, busted, remaining: nextRemaining };
  },

  canSubmitVisit() {
    if (!this.liveScoringKeypadEnabled()) return false;
    if (this.isMaxDartsPerLegReached()) return false;
    const outcome = this.scoreOutcome();
    if (!outcome.valid) return false;
    return true;
  },

  openCheckoutConfirmDialog() {
    this.checkoutConfirmPending = {
      scoreInput: String(this.scoreInput ?? ''),
      checkoutDarts: String(this.checkoutDarts || ''),
      masterFinishType: String(this.masterFinishType || 'master'),
    };
    this.checkoutConfirmDarts = ['1', '2', '3'].includes(String(this.checkoutDarts || ''))
      ? String(this.checkoutDarts)
      : '3';
    this.checkoutConfirmOpen = true;
  },

  cancelCheckoutConfirm() {
    const pending = this.checkoutConfirmPending || null;
    if (pending) {
      this.scoreInput = String(pending.scoreInput ?? '');
      this.checkoutDarts = String(pending.checkoutDarts || '');
      this.masterFinishType = String(pending.masterFinishType || 'master');
    }
    this.checkoutConfirmPending = null;
    this.checkoutConfirmOpen = false;
  },

  async confirmCheckoutConfirm() {
    if (!this.checkoutConfirmOpen) return;
    this.checkoutDarts = String(this.checkoutConfirmDarts || '3');
    this.checkoutConfirmPending = null;
    this.checkoutConfirmOpen = false;
    await this.recordThrow(null, { skipCheckoutConfirm: true });
  },

  async saveCurrentPlayers() {
    if (!this.canScore() || this.fixtureScoringFrozen()) return;
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/players`, {
        method: 'PATCH',
        body: this.sessionBody({
          homePlayers: this.selectedHomePlayers.slice(0, this.expectedPlayers()),
          awayPlayers: this.selectedAwayPlayers.slice(0, this.expectedPlayers()),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to save game players');
      this.applyRemoteSession(data.session);
      this.activeThrower.home = this.defaultThrowerFor('home');
      this.activeThrower.away = this.defaultThrowerFor('away');
      Alpine.store('toastr').success('Game players updated', 2500);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to save game players', 3000);
    } finally {
      this.saving = false;
    }
  },

  fixtureThrowErrorMessage(code) {
    const key = String(code || '').trim();
    const map = {
      auth_required: 'Sign in or rescan the fixture QR from the venue tablet.',
      scoring_locked: 'Fixture setup is not ready for scoring yet.',
      setup_required: 'Complete fixture setup before scoring.',
      leg_complete: 'This leg is already finished.',
      invalid_side: 'It is not this side\'s turn — refresh and try again.',
      max_darts_per_leg: 'Leg dart limit reached.',
      nothing_to_undo: 'Nothing to undo on this leg.',
      throw_failed: 'Could not record that score.',
      undo_failed: 'Could not undo the last score.',
    };
    if (map[key]) return map[key];
    if (!key) return 'Could not reach the scoring server. Check Wi‑Fi and try again.';
    if (key.includes(' ')) return key;
    return key.replace(/_/g, ' ');
  },

  fixtureApiErrorFromResponse(data, res) {
    return this.fixtureThrowErrorMessage(
      data?.message || data?.error || (res?.status === 401 ? 'auth_required' : ''),
    );
  },

  async recordThrow(side = null, opts = {}) {
    const game = this.currentGame();
    if (!game) return;
    if (this.isMobileFixtureQrScorer() && !getAccessToken()) {
      try {
        await this.ensureMobileFixtureKioskAuth();
      } catch (e) {
        Alpine.store('toastr')?.error?.(e?.message || 'Could not authorize scoring.', 4000);
        return;
      }
    }
    if (!this.canScore() && this.embedMode && !this.fromVenueKiosk && !this.fixtureSessionIsFinalized()) {
      const claimed = await this.ensureControllerClaim(true);
      if (!claimed) {
        Alpine.store('toastr')?.error?.('Take control on this device to score.', 3500);
        return;
      }
      this.disconnectEventStream();
      this.ensureEventStream();
    }
    if (!this.liveScoringKeypadEnabled()) return;
    if (!this.canSubmitVisit()) return;
    if (this.isMaxDartsPerLegReached()) {
      Alpine.store('toastr').error(`House cap reached (${this.currentGameMaxDartsPerLeg()} darts this leg).`, 3500);
      return;
    }
    if (this.saving) return;

    const score = Number(String(this.scoreInput ?? '').trim() || 0);
    if (!Number.isFinite(score) || score < 0) return;
    const checkout = this.scoreOutcome().checkout;
    if (checkout && this.sessionCheckoutRule() !== 'straight-out' && !opts?.skipCheckoutConfirm) {
      this.openCheckoutConfirmDialog();
      return;
    }

    const targetSide = side || this.currentTurnSide();
    this.disarmUndo();
    const playerId = String(this.activeThrower[targetSide] || this.defaultThrowerFor(targetSide) || '');
    const darts = checkout ? Number(this.checkoutDarts || 3) : 3;
    const event = buildLocalEvent('visit.recorded', { side: targetSide, playerId, score, darts, checkout }, this.localSession);
    this.saving = true;
    this.localSession = enqueueFixtureLiveEvent(this.localSession, event);
    saveFixtureLiveSession(window.localStorage, this.localSession);
    try {
      const rule = this.sessionCheckoutRule();
      const clientCheckout = checkout && (rule === 'straight-out'
        || ['1', '2', '3'].includes(String(this.checkoutDarts || '')));
      const throwBody = {
        side: targetSide,
        playerId,
        score,
        darts,
        checkout: clientCheckout,
      };
      if (rule === 'master-out' && clientCheckout) {
        throwBody.finishType = String(this.masterFinishType || 'master').trim() || 'master';
      }
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/throw`, {
        method: 'POST',
        body: this.sessionBody(throwBody),
        headers: this.isMobileFixtureQrScorer() ? this.fixtureKioskRequestHeaders() : undefined,
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        if (data?.message === 'max_darts_per_leg') {
          throw new Error(`House cap reached (${data.maxDartsPerLeg || this.currentGameMaxDartsPerLeg()} darts this leg).`);
        }
        throw new Error(this.fixtureApiErrorFromResponse(data, res) || 'Failed to record throw');
      }
      this.localSession = acknowledgeFixtureLiveEvents(this.localSession, event.clientSeq, data.session?.revision || null);
      this.applyRemoteSession(data.session);
      this.scoreInput = '';
      this.editingVisitIndex = -1;
      this.checkoutDarts = '';
      this.masterFinishType = 'master';
    } catch (e) {
      this.recoveryNotice = 'Throw saved locally. Waiting to sync.';
      Alpine.store('toastr').error(e.message || 'Throw saved locally. Waiting to sync.', 3000);
    } finally {
      this.saving = false;
    }
  },

  armUndo() {
    this.undoArmed = true;
    Alpine.store('toastr').info('Tap Undo again to confirm', 1800);
    if (this.undoArmTimer) window.clearTimeout(this.undoArmTimer);
    this.undoArmTimer = window.setTimeout(() => {
      this.undoArmed = false;
      this.undoArmTimer = null;
    }, 3000);
  },

  disarmUndo() {
    this.undoArmed = false;
    if (this.undoArmTimer) {
      window.clearTimeout(this.undoArmTimer);
      this.undoArmTimer = null;
    }
  },

  async undoThrow() {
    if (!this.liveScoringKeypadEnabled()) return;
    if (this.saving) return;
    if (!this.undoArmed) {
      this.armUndo();
      return;
    }
    this.disarmUndo();
    const event = buildLocalEvent('visit.undone', {}, this.localSession);
    this.saving = true;
    this.localSession = enqueueFixtureLiveEvent(this.localSession, event);
    saveFixtureLiveSession(window.localStorage, this.localSession);
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/undo`, {
        method: 'POST',
        body: this.sessionBody(),
        headers: this.isMobileFixtureQrScorer() ? this.fixtureKioskRequestHeaders() : undefined,
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(this.fixtureApiErrorFromResponse(data, res) || 'Failed to undo throw');
      this.localSession = acknowledgeFixtureLiveEvents(this.localSession, event.clientSeq, data.session?.revision || null);
      this.applyRemoteSession(data.session);
      Alpine.store('toastr').success('Last score undone', 1800);
    } catch (e) {
      this.recoveryNotice = 'Undo saved locally. Waiting to sync.';
      Alpine.store('toastr').error(e.message || 'Undo saved locally. Waiting to sync.', 3000);
    } finally {
      this.saving = false;
    }
  },

  async finishLeg() {
    if (!this.canScore() || this.fixtureScoringFrozen()) return;
    const winnerSide = this.currentLeg()?.winnerSide || this.leadingSide();
    if (!winnerSide) return;
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/finish-leg`, {
        method: 'POST',
        body: this.sessionBody({ winnerSide }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to finish leg');
      this.applyRemoteSession(data.session);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to finish leg', 3000);
    } finally {
      this.saving = false;
    }
  },

  async finishGame() {
    if (!this.canScore() || this.fixtureScoringFrozen()) return;
    const winnerSide = this.currentGame()?.winnerSide || this.leadingGameSide();
    if (!winnerSide) return;
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/finish-game`, {
        method: 'POST',
        body: this.sessionBody({ winnerSide }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to finish game');
      this.applyRemoteSession(data.session);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to finish game', 3000);
    } finally {
      this.saving = false;
    }
  },

  async goToGame(index) {
    if (!this.setupReady()) return;
    this.cancelVisitEdit();
    this.disarmUndo();
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/current-game`, {
        method: 'POST',
        body: this.sessionBody({ gameIndex: index, currentGameIndex: index }),
        headers: this.isMobileFixtureQrScorer() ? this.fixtureKioskRequestHeaders() : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        if (res.status === 409 && String(data?.code || '') === 'PENDING_NEXT_MATCH') {
          Alpine.store('toastr').info('Confirm the next match first (dialog at top of the page).', 4000);
        } else {
          Alpine.store('toastr').error(data?.message || 'Failed to change game', 3000);
        }
        return;
      }
      this.applyRemoteSession(data.session);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to change game', 3000);
    } finally {
      this.saving = false;
    }
  },

  async acceptPendingNextMatch() {
    if (!this.session?._id || !this.hasPendingNextMatch()) return false;
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/accept-next`, {
        method: 'POST',
        body: this.sessionBody(),
        headers: this.isMobileFixtureQrScorer() ? this.fixtureKioskRequestHeaders() : undefined,
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Could not start the next match');
      this.applyRemoteSession(data.session);
      this.scoreInput = '';
      this.checkoutDarts = '';
      this.editingVisitIndex = -1;
      Alpine.store('toastr').success(`Now scoring: ${this.gameLabel(this.currentGame())}`, 2400);
      return true;
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Could not start the next match', 3500);
      return false;
    } finally {
      this.saving = false;
    }
  },

  async undoLastScoreFromMatchComplete() {
    if (!this.canScore()) {
      Alpine.store('toastr')?.error?.('Take scoring control on this device to undo.', 3000);
      return;
    }
    this.disarmUndo();
    if (this.saving) return;
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/undo`, {
        method: 'POST',
        body: this.sessionBody(),
        headers: this.isMobileFixtureQrScorer() ? this.fixtureKioskRequestHeaders() : undefined,
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(this.fixtureApiErrorFromResponse(data, res) || 'Failed to undo throw');
      this.applyRemoteSession(data.session);
      this.scoreInput = '';
      this.checkoutDarts = '';
      this.editingVisitIndex = -1;
      Alpine.store('toastr').success('Last score undone', 1800);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Could not undo the last score', 3500);
    } finally {
      this.saving = false;
    }
  },

  async continueAfterMatchComplete() {
    if (this.canContinueToNextMatch()) {
      await this.acceptPendingNextMatch();
      return;
    }
    if (this.fromVenueKiosk && this.isLastMatchJustFinished()) {
      this.returnVenueKioskFixtureHub();
    }
  },

  async submitSession() {
    if (!this.canScore() || !this.allGamesComplete()) return;
    this.disarmUndo();
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/submit`, { method: 'POST', body: this.sessionBody() });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to submit fixture session');
      this.applyRemoteSession(data.session);
      const pending = String(data.session?.status || data.status || '') === 'pending_review';
      Alpine.store('toastr').success(
        pending
          ? 'Submitted for admin review — standings update after approval.'
          : 'Fixture session submitted',
        4000,
      );
      if (this.embedMode && this.fromVenueKiosk) {
        this.postVenueKioskShellMessage({ type: 'fixture-scorer-leave', submitted: true });
      }
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to submit fixture session', 3000);
    } finally {
      this.saving = false;
    }
  },

  async syncScoresheetDraft() {
    if (!this.session?._id) return;
    if (!this.canScore() && !this.fixtureSessionIsFinalized()) return;
    this.scoresheetSyncing = true;
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/scoresheet-draft`, {
        method: 'POST',
        body: this.sessionBody(),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to sync scoresheet draft');
      this.applyRemoteSession(data.session);
      this.syncedScoresheet = data.scoresheet || null;
      Alpine.store('toastr').success('Scoresheet draft updated from live session', 3000);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to sync scoresheet draft', 3000);
    } finally {
      this.scoresheetSyncing = false;
      this.saving = false;
    }
  },

  async refreshCompletionScoresheetDraft() {
    if ((!this.canScore() && !this.fixtureSessionIsFinalized()) || !this.session?._id || this.scoresheetSyncing) return;
    try {
      await this.syncScoresheetDraft();
    } catch { }
  },

  finalFixtureScore() {
    return {
      home: Number(this.session?.score?.homePoints || 0),
      away: Number(this.session?.score?.awayPoints || 0),
    };
  },

  scoresheetPhotoReady() {
    return !!String(this.syncedScoresheet?.uploadUrl || '');
  },

  async openScoresheetPhotoStep() {
    await this.refreshCompletionScoresheetDraft();
    const id = String(this.syncedScoresheet?.id || '');
    if (!id) return;
    routerNavigate(`/admin/scoresheet/${id}/edit?tab=photo`);
  },

  async submitScoresheetForReview() {
    await this.refreshCompletionScoresheetDraft();
    const id = String(this.syncedScoresheet?.id || '');
    if (!id) {
      Alpine.store('toastr').error('Create the scoresheet draft first', 2500);
      return;
    }
    if (!this.scoresheetPhotoReady()) {
      Alpine.store('toastr').error('Upload the scoresheet photo before submitting for review', 3000);
      return;
    }
    this.saving = true;
    try {
      const res = await apiFetch(`/scoresheets/${id}`, {
        method: 'PATCH',
        body: { status: 'submitted' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Unable to submit scoresheet for review');
      this.syncedScoresheet = {
        ...(this.syncedScoresheet || {}),
        id,
        status: 'submitted',
      };
      Alpine.store('toastr').success('Scoresheet submitted for review', 3000);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to submit scoresheet for review', 3000);
    } finally {
      this.saving = false;
    }
  },

  scheduleSaveCurrentPlayers() {
    if (!this.canScore() || this.fixtureScoringFrozen()) return;
    if (this.playersSaveTimer) window.clearTimeout(this.playersSaveTimer);
    this.playersSaveTimer = window.setTimeout(() => {
      this.playersSaveTimer = null;
      this.saveCurrentPlayers();
    }, 400);
  },

  editVisit(visit, index) {
    if (!this.setupReady() || this.fixtureScoringFrozen()) return;
    this.scoreInput = String(Number(visit?.score || 0));
    this.editingVisitIndex = Number(index);
    const side = String(visit?.side || '');
    if (side && ['home', 'away'].includes(side) && visit?.playerId) {
      this.activeThrower[side] = String(visit.playerId);
    }
    window.requestAnimationFrame(() => {
      try {
        if (this.scoringWindow) {
          const inp = this.$refs?.scoreInputField;
          inp?.focus?.({ preventScroll: true });
          if (inp && typeof inp.select === 'function') inp.select();
        } else {
          const cin = this.$refs?.consoleHistoryScoreInput;
          cin?.focus?.({ preventScroll: true });
          if (cin && typeof cin.select === 'function') cin.select();
        }
      } catch { }
    });
  },

  cancelVisitEdit() {
    this.editingVisitIndex = -1;
    this.scoreInput = '';
    this.checkoutDarts = '';
  },

  async saveVisitEdit() {
    if (!this.session?._id || this.editingVisitIndex < 0) return;
    const fromConsole = !this.scoringWindow;
    const okToSave = fromConsole ? this.consoleVisitEditSaveReady() : this.liveScoringKeypadEnabled();
    if (!okToSave) {
      if (fromConsole) {
        Alpine.store('toastr').info('Finish setup, pick players for this game, or wait until live scoring is available.', 3200);
      } else {
        Alpine.store('toastr').info('Take scoring control on this device to save edits.', 3200);
      }
      return;
    }
    this.disarmUndo();
    const score = Number(this.scoreInput || 0);
    if (!Number.isFinite(score) || score < 0) return;
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/visits/${this.editingVisitIndex}`, {
        method: 'PATCH',
        body: this.sessionBody({
          score,
          ...(fromConsole ? { matchConsoleVisitEdit: true } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to update throw');
      this.applyRemoteSession(data.session);
      this.scoreInput = '';
      this.editingVisitIndex = -1;
      Alpine.store('toastr').success('Throw updated', 2500);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to update throw', 3000);
    } finally {
      this.saving = false;
    }
  },

  async removeVisitEdit() {
    if (!this.session?._id || this.editingVisitIndex < 0) return;
    const fromConsole = !this.scoringWindow;
    const ok = fromConsole ? this.consoleVisitEditSaveReady() : this.liveScoringKeypadEnabled();
    if (!ok) {
      if (fromConsole) {
        Alpine.store('toastr').info('Finish setup, pick players for this game, or wait until live scoring is available.', 3200);
      } else {
        Alpine.store('toastr').info('Take scoring control on this device to remove throws.', 3200);
      }
      return;
    }
    if (!window.confirm('Remove this throw from the leg? Remaining scores will be recalculated.')) return;
    this.disarmUndo();
    this.saving = true;
    try {
      const res = await apiFetch(`/fixture-sessions/${this.session._id}/games/current/visits/${this.editingVisitIndex}`, {
        method: 'DELETE',
        body: this.sessionBody(
          fromConsole ? { matchConsoleVisitEdit: true } : {},
        ),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to remove throw');
      this.applyRemoteSession(data.session);
      this.scoreInput = '';
      this.checkoutDarts = '';
      this.editingVisitIndex = -1;
      Alpine.store('toastr').success('Throw removed', 2500);
    } catch (e) {
      Alpine.store('toastr').error(e.message || 'Unable to remove throw', 3000);
    } finally {
      this.saving = false;
    }
  },

  openScoresheetDraft() {
    const id = String(this.syncedScoresheet?.id || '');
    if (!id) return;
    routerNavigate(`/admin/scoresheet/${id}/edit`);
  },

  manualScoresheetLabel() {
    const id = String(this.syncedScoresheet?.id || '');
    return id ? 'Open scoresheet' : 'Manual scoresheet';
  },

  async openManualScoresheet() {
    if (!this.session?._id) {
      Alpine.store('toastr')?.info?.('Fixture session is still loading.', 2500);
      return;
    }
    try {
      await this.syncScoresheetDraft();
    } catch {
      /* toast from syncScoresheetDraft */
    }
    const id = String(this.syncedScoresheet?.id || '');
    if (id) {
      routerNavigate(`/admin/scoresheet/${id}/edit`);
      return;
    }
    Alpine.store('toastr')?.info?.('No scoresheet draft yet — score on the kiosk or sync again after the first leg.', 4000);
  },

  openFixtureLiveView() {
    if (!this.fixtureId) return;
    routerNavigate(`/admin/fixtures/${this.fixtureId}/live`);
  },

  leadingSide() {
    const leg = this.currentLeg();
    if (!leg) return '';
    if (Number(leg.homeRemaining || 0) === Number(leg.awayRemaining || 0)) return '';
    return Number(leg.homeRemaining || 0) < Number(leg.awayRemaining || 0) ? 'home' : 'away';
  },

  leadingGameSide() {
    const game = this.currentGame();
    if (!game) return '';
    const homeLegs = (game.legs || []).filter((leg) => leg.winnerSide === 'home').length;
    const awayLegs = (game.legs || []).filter((leg) => leg.winnerSide === 'away').length;
    if (homeLegs === awayLegs) return '';
    return homeLegs > awayLegs ? 'home' : 'away';
  },

  allQuickPresetPool() {
    return [...SCORER_QUICK_PRESET_POOL];
  },

  quickPresetButtonLabel,

  ensureQuickScoreDefaults() {
    const top = Array.isArray(this.quickScoreTop) ? this.quickScoreTop : [];
    if (top.length > 0) return;
    const norm = normalizeScorerQuickConfig({
      top,
      more: Array.isArray(this.quickScoreMore) ? this.quickScoreMore : [],
    });
    this.quickScoreTop = [...norm.top];
    this.quickScoreMore = [...norm.more];
  },

  visibleQuickPresets() {
    this.ensureQuickScoreDefaults();
    return this.quickScoreTop.slice(0, 4);
  },

  paddedVisibleQuickPresets() {
    const v = this.visibleQuickPresets();
    return [0, 1, 2, 3].map((i) => (v[i] != null ? v[i] : null));
  },

  keypadQuickTopSlots() {
    this.ensureQuickScoreDefaults();
    const padded = this.paddedVisibleQuickPresets();
    const editing = this.editingVisitIndex >= 0;
    const rem = this.currentTurnRemaining();
    const active = this.liveScoringKeypadEnabled() && !editing;
    return withNearCheckoutNsOnFourthQuickTop(padded, rem, { editingVisit: editing, active });
  },

  onQuickTopSlotClick(preset) {
    if (preset && typeof preset === 'object' && preset.ns) {
      void this.quickTopNoScoreAndSubmit();
      return;
    }
    this.setScoreInput(preset);
  },

  async quickTopNoScoreAndSubmit() {
    if (!this.liveScoringKeypadEnabled() || this.editingVisitIndex >= 0) return;
    this.setScoreInput(0);
    await this.$nextTick?.();
    await this.recordThrow();
  },

  moreQuickPresets() {
    return mergeMoreQuickPresets(this.quickScoreMore);
  },

  keypadQuickOverlaySlots() {
    this.ensureQuickScoreDefaults();
    return buildKeypadQuickOverlaySlots(
      this.keypadQuickOverlayCols(),
      this.quickScoreTop,
      this.quickScoreMore,
    );
  },

  keypadQuickOverlaySlotKey(preset, index) {
    if (preset == null) return `sfqk-empty-${index}`;
    return `sfqk-${index}-${this.quickMorePresetKey(preset)}`;
  },

  hasMoreQuickPresets() {
    return this.moreQuickPresets().length > 0;
  },

  allQuickTop() {
    return this.keypadQuickTopSlots();
  },

  quickPresetLabel(preset) {
    return quickPresetButtonLabel(preset);
  },

  isNsQuickPreset(preset) {
    return isNsQuickPresetValue(preset);
  },

  quickMorePresetKey(preset) {
    return this.isNsQuickPreset(preset) ? 'qkm-ns' : `qkm-${preset}`;
  },

  toggleQuickMorePanel(ev) {
    if (this.quickMoreOpen) {
      this.closeQuickMore();
      return;
    }
    if (typeof this.closeScorerMobileHistory === 'function') this.closeScorerMobileHistory();
    this.quickMoreOpen = true;
    this.quickScoresExpanded = true;
    this._quickMoreSuppressOutsideUntil = Date.now() + 800;
    this.quickMoreFixedPopover =
      !this.embedMode &&
      !this.scorerUseKeypadQuickOverlay() &&
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(min-width: 640px)').matches;
    const afterOpen = () => {
      if (this.quickMoreFixedPopover) {
        this.positionQuickMorePanel(ev);
        requestAnimationFrame(() => this.positionQuickMorePanel(ev));
      } else if (!this.scorerUseKeypadQuickOverlay()) {
        this.quickMorePanelStyle = '';
        Alpine.nextTick(() => {
          this.$refs?.quickMorePanel?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
        });
      } else {
        this.quickMorePanelStyle = '';
      }
    };
    if (typeof Alpine !== 'undefined' && Alpine.nextTick) Alpine.nextTick(afterOpen);
    else this.$nextTick?.(afterOpen);
  },

  /**
   * Run keypad toggle actions on pointerup so the same tap does not immediately
   * trip @click.outside on the quick-score overlay.
   */
  keypadUiPointer(ev, fn) {
    const b = ev?.button;
    if (b != null && b !== 0) return;
    const el = ev?.currentTarget ?? null;
    const startX = Number(ev?.clientX ?? 0);
    const startY = Number(ev?.clientY ?? 0);
    let cancelled = false;

    const onMove = (moveEv) => {
      if (cancelled) return;
      const dx = Number(moveEv?.clientX ?? 0) - startX;
      const dy = Number(moveEv?.clientY ?? 0) - startY;
      if (Math.hypot(dx, dy) > 12) cancelled = true;
    };
    const onEnd = () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onEnd, true);
      document.removeEventListener('pointercancel', onEnd, true);
      if (cancelled) return;
      try {
        ev.preventDefault();
      } catch {
        /* ignore */
      }
      const now =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now();
      this._keypadUiPointerTs = now;
      this._keypadUiPointerEl = el;
      fn();
      requestScorerHaptic('light');
    };

    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', onEnd, true);
    document.addEventListener('pointercancel', onEnd, true);
  },

  keypadUiClick(ev, fn) {
    const now =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
    const el = ev?.currentTarget ?? null;
    const sameTarget = el && el === this._keypadUiPointerEl;
    if (sameTarget && now - this._keypadUiPointerTs < 500) return;
    fn();
  },

  positionQuickMorePanel(ev) {
    if (!this.quickMoreFixedPopover || this.scorerUseKeypadQuickOverlay()) return;
    const btn = ev?.currentTarget;
    if (!btn?.getBoundingClientRect) return;
    const r = btn.getBoundingClientRect();
    const vw = globalThis.innerWidth || 400;
    const vh = globalThis.innerHeight || 600;
    const panelW = Math.min(300, vw - 16);
    const gap = 8;
    const panelEl = this.$refs?.quickMorePanel;
    const estRows = Math.ceil(this.moreQuickPresets().length / 4);
    const estH = Math.max(120, (estRows * 44) + 56);
    const measured = panelEl ? Math.max(panelEl.scrollHeight || 0, panelEl.offsetHeight || 0) : 0;
    const maxAllowedH = Math.max(160, vh - (gap * 2));
    const panelH = Math.min(Math.max(estH, measured), maxAllowedH);
    let left = r.left + r.width / 2 - panelW / 2;
    const roomBelow = (vh - gap) - (r.bottom + gap);
    const roomAbove = (r.top - gap) - gap;
    let top = r.bottom + gap;
    if (roomBelow < panelH && roomAbove > roomBelow) {
      top = r.top - gap - panelH;
    }
    left = Math.max(gap, Math.min(left, vw - panelW - gap));
    top = Math.max(gap, Math.min(top, vh - panelH - gap));
    this.quickMorePanelStyle = `position:fixed;left:${Math.round(left)}px;top:${Math.round(top)}px;width:${Math.round(panelW)}px;max-height:${Math.round(panelH)}px;overflow-y:auto;z-index:10050;`;
  },

  closeQuickMore() {
    if (this._quickMoreCloseTimer) {
      clearTimeout(this._quickMoreCloseTimer);
      this._quickMoreCloseTimer = null;
    }
    this.quickMoreOpen = false;
    this.quickScoresExpanded = false;
    this.quickMorePanelStyle = '';
    this.quickMoreFixedPopover = true;
  },

  onQuickMoreClickOutside(ev) {
    if (Date.now() < (this._quickMoreSuppressOutsideUntil || 0)) return;
    const scoreBtn = this.$refs?.moreQuickBtn;
    const overlay = this.$refs?.quickOverlayPanel;
    const desktopPanel = this.$refs?.quickMorePanel;
    const target = ev?.target;
    if (scoreBtn && target && scoreBtn.contains(target)) return;
    if (overlay && target && overlay.contains(target)) return;
    if (desktopPanel && target && desktopPanel.contains(target)) return;
    this.closeQuickMore();
  },

  scheduleCloseQuickMore(ms = 200) {
    if (this._quickMoreCloseTimer) clearTimeout(this._quickMoreCloseTimer);
    this._quickMoreCloseTimer = setTimeout(() => {
      this._quickMoreCloseTimer = null;
      this.closeQuickMore();
    }, ms);
  },

  pickQuickMorePreset(preset) {
    if (preset && typeof preset === 'object') return;
    this.setScoreInput(preset);
    this.scheduleCloseQuickMore();
  },

  async pickQuickMoreNoScore() {
    if (!this.liveScoringKeypadEnabled()) return;
    this.setScoreInput(0);
    this.scheduleCloseQuickMore();
    await this.$nextTick?.();
    await this.recordThrow();
  },

  persistQuickScores() {
    saveScorerQuickConfig(FIXTURE_SCORER_QUICK_LS, { top: this.quickScoreTop, more: this.quickScoreMore });
  },

  toggleQuickTop(preset) {
    if (this.fixtureScoringFrozen()) return;
    const p = Number(preset);
    const top = [...this.quickScoreTop];
    const more = [...this.quickScoreMore];
    const i = top.indexOf(p);
    if (i >= 0) {
      top.splice(i, 1);
    } else if (top.length >= 4) {
      Alpine.store('toastr')?.info?.('At most 4 scores on the top row.', 2800);
      return;
    } else {
      const mi = more.indexOf(p);
      if (mi >= 0) more.splice(mi, 1);
      top.push(p);
    }
    this.quickScoreTop = top;
    this.quickScoreMore = more;
    this.persistQuickScores();
  },

  toggleQuickMore(preset) {
    if (this.fixtureScoringFrozen()) return;
    const p = Number(preset);
    if (this.quickScoreTop.includes(p)) return;
    const more = [...this.quickScoreMore];
    const i = more.indexOf(p);
    if (i >= 0) more.splice(i, 1);
    else more.push(p);
    more.sort((a, b) => a - b);
    this.quickScoreMore = more;
    this.persistQuickScores();
  },

  resetQuickScoresToDefault() {
    if (this.fixtureScoringFrozen()) return;
    const d = defaultScorerQuickConfig();
    this.quickScoreTop = [...d.top];
    this.quickScoreMore = [...d.more];
    this.persistQuickScores();
    Alpine.store('toastr')?.success?.('Quick scores reset', 2000);
  },

  allGamesComplete() {
    return (this.session?.games || []).length > 0
      && (this.session?.games || []).every((game) => String(game?.status || '') === 'complete' && ['home', 'away'].includes(String(game?.winnerSide || '')));
  },

  currentGameHighScore() {
    const visits = (this.currentGame()?.legs || []).flatMap((leg) => leg?.visits || []);
    return visits.reduce((best, visit) => visit?.busted ? best : Math.max(best, Number(visit?.score || 0)), 0);
  },

  isKeyboardTargetEditable(target) {
    if (!target || !(target instanceof HTMLElement)) return false;
    const tag = String(target.tagName || '').toLowerCase();
    if (target.isContentEditable) return true;
    if ((tag === 'input' || tag === 'textarea') && target.hasAttribute('readonly')) return false;
    return ['input', 'select', 'textarea'].includes(tag);
  },

  async onScoreKeydown(event) {
    if (!this.session || this.loading) return;
    if (!this.setupReady()) return;
    if (event.repeat || event.defaultPrevented) return;
    const activeEl = document.activeElement;
    if (activeEl instanceof HTMLElement && this.isKeyboardTargetEditable(activeEl)) return;
    if (this.isKeyboardTargetEditable(event.target)) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const key = String(event.key || '');
    if (key === 'Escape') {
      if (this.playerStatsOpen) {
        event.preventDefault();
        this.closePlayerStatsDialog();
        return;
      }
      if (this.editingVisitIndex >= 0) {
        event.preventDefault();
        this.cancelVisitEdit();
        return;
      }
    }

    if (!this.liveScoringKeypadEnabled()) return;

    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      event.stopPropagation();
      this.appendDigit(key);
      return;
    }

    if (/^Numpad[0-9]$/.test(key)) {
      event.preventDefault();
      event.stopPropagation();
      this.appendDigit(key.replace('Numpad', ''));
      return;
    }

    if (key === 'Backspace') {
      event.preventDefault();
      event.stopPropagation();
      this.backspace();
      return;
    }

    if (key === 'Enter' || key === 'NumpadEnter') {
      event.preventDefault();
      event.stopPropagation();
      if (!this.liveScoringKeypadEnabled()) return;
      if (this.editingVisitIndex >= 0) {
        await this.saveVisitEdit();
        return;
      }
      if (this.canSubmitVisit()) {
        await this.recordThrow();
      }
      return;
    }
  },

  historyVisits() {
    const visits = this.currentLeg()?.visits || [];
    return visits.map((visit, index) => ({ visit, index })).reverse();
  },

  historyVisitRowKey(entry) {
    const v = entry?.visit || {};
    const t = v.createdAt ? String(v.createdAt) : 'x';
    const i = Number(entry?.index);
    return `hv-${Number.isFinite(i) ? i : 0}-${t}-${String(v.side || '')}`;
  },

  historyVisitActorLabel(visit) {
    const name = String(this.playerName(visit?.playerId) || '').trim();
    if (name) return name;
    return visit?.side === 'away' ? (this.fixture?.awayTeamName || 'Away') : (this.fixture?.homeTeamName || 'Home');
  },

  /** Latest visits for one side on the current leg (newest first), with leg visit index for editVisit. */
  lastVisitsForSide(side, limit = 3) {
    const cap = Math.max(0, Number(limit) || 3);
    const all = this.currentLeg()?.visits || [];
    const forSide = all.map((visit, index) => ({ visit, index })).filter(({ visit }) => String(visit?.side || '') === String(side || ''));
    return forSide.slice(-cap).reverse();
  },

  visitCompactDisplay(visit) {
    if (!visit) return '';
    if (visit.busted) return 'Bust';
    const score = Number(visit.score || 0);
    if (visit.checkout) {
      const d = Math.max(1, Number(visit.darts || 3));
      return `${score}/${d}d`;
    }
    return String(score);
  },

  currentLegDartsThrown() {
    const visits = this.currentLeg()?.visits || [];
    return visits.reduce((total, visit) => total + Math.max(0, Number(visit?.darts || 3)), 0);
  },

  isEditingHistoryEntry(index) {
    return Number(this.editingVisitIndex) === Number(index);
  },

  appendDigit(digit) {
    if (!this.liveScoringKeypadEnabled()) return;
    const nextValue = `${this.scoreInput || ''}${digit}`.slice(0, 3);
    const numeric = Math.min(180, Number(nextValue || 0));
    this.scoreInput = String(Number.isFinite(numeric) ? numeric : '');
    this.checkoutDarts = '';
  },

  setScoreInput(value) {
    if (!this.liveScoringKeypadEnabled()) return;
    if (value && typeof value === 'object') return;
    const numeric = Math.min(180, Number(value || 0));
    this.scoreInput = String(Number.isFinite(numeric) ? numeric : '');
    this.checkoutDarts = '';
  },

  clearScoreInput() {
    if (!this.liveScoringKeypadEnabled()) return;
    this.scoreInput = '';
    this.checkoutDarts = '';
  },

  backspace() {
    if (!this.liveScoringKeypadEnabled()) return;
    this.scoreInput = String(this.scoreInput || '').slice(0, -1);
    this.checkoutDarts = '';
  },

  gameLabel(game) {
    if (!game) return '—';
    const type = String(game?.type || 'singles');
    if (type === 'beer') return `${game.order}. Beer Leg`;
    return `${game.order}. ${type.replace(/^./, (c) => c.toUpperCase())}`;
  },

  navigatorGameReady(game) {
    const type = String(game?.type || 'singles');
    if (type === 'beer') return this.assignmentSetupReady();
    return this.setupGameReady(game);
  },

  navigatorGameHasStarted(game) {
    const legs = Array.isArray(game?.legs) ? game.legs : [];
    return legs.some((leg) => {
      const visits = Array.isArray(leg?.visits) ? leg.visits : [];
      return visits.length > 0 || ['home', 'away'].includes(String(leg?.winnerSide || ''));
    });
  },

  /** Fixture session is live on the venue (captains confirmed or session marked live). */
  fixtureScoringStarted() {
    if (this.fixtureSessionIsFinalized()) return false;
    if (this.setupReady()) return true;
    const st = String(this.session?.status || this.fixture?.status || '').toLowerCase();
    return st === 'live' || st === 'in_progress' || st === 'started';
  },

  /** Current or spotlight game is actively being played (session live on this match). */
  navigatorGameInProgress(game) {
    if (!game || this.navigatorGameIsComplete(game)) return false;
    if (this.navigatorGameHasStarted(game)) return true;
    const idx = Number(this.setupGameIndex(game));
    const currentIndex = Number(this.session?.currentGameIndex || 0);
    return idx === currentIndex && this.fixtureScoringStarted();
  },

  gameLegWins(game) {
    const legs = Array.isArray(game?.legs) ? game.legs : [];
    const home = legs.filter((leg) => leg?.winnerSide === 'home').length;
    const away = legs.filter((leg) => leg?.winnerSide === 'away').length;
    return { home, away };
  },

  gameLegsNeeded(game) {
    return Math.floor(Number(game?.bestOfLegs || 3) / 2) + 1;
  },

  navigatorGameIsComplete(game) {
    if (!game) return false;
    if (String(game?.status || '').toLowerCase() === 'complete') return true;
    const wins = this.gameLegWins(game);
    const needed = this.gameLegsNeeded(game);
    return wins.home >= needed || wins.away >= needed;
  },

  navigatorGameStatus(game) {
    if (!this.navigatorGameReady(game)) return 'Unassigned';
    if (this.navigatorGameIsComplete(game)) return 'Complete';
    const index = Number(this.setupGameIndex(game));
    const currentIndex = Number(this.session?.currentGameIndex || 0);
    if (index === currentIndex) {
      if (this.navigatorGameInProgress(game)) return 'In progress';
      return 'Ready';
    }
    if (this.navigatorGameHasStarted(game)) return 'Resume';
    return 'Ready';
  },

  /** Fixture setup: one game-level status pill (not repeated on each team card). */
  setupGameStatusLabel(game) {
    if (!game) return '—';
    if (!this.navigatorGameReady(game)) return 'Awaiting lineup';
    const wins = this.gameLegWins(game);
    const legsStr = `${wins.home}-${wins.away}`;
    if (this.navigatorGameIsComplete(game)) return `Complete · ${legsStr}`;
    const idx = Number(this.setupGameIndex(game));
    const currentIndex = Number(this.session?.currentGameIndex || 0);
    if (idx === currentIndex) {
      if (this.navigatorGameInProgress(game)) return `In progress · ${legsStr}`;
      return 'Next up';
    }
    if (this.navigatorGameHasStarted(game)) return `Paused · ${legsStr}`;
    return 'Upcoming';
  },

  /** Admin match-console hub: same labels as setup cards. */
  hubSetupGameStatusLabel(game) {
    return this.setupGameStatusLabel(game);
  },

  hubNavigatorGameSubtitle(game) {
    if (!game || !this.navigatorGameReady(game)) return '';
    const wins = this.gameLegWins(game);
    if (this.navigatorGameIsComplete(game)) return `Final ${wins.home}-${wins.away}`;
    if (this.navigatorGameInProgress(game)) return `Legs ${wins.home}-${wins.away}`;
    return '';
  },

  /** Matches Games sidebar pill when status is Complete (non-current row). */
  setupGameCompleteStatusPillClass() {
    return 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300/80 dark:bg-emerald-400/35 dark:text-emerald-950 dark:ring-emerald-300/70';
  },

  /** Fixture setup: whole game card — subtle glow when this match is live (current + scoring started). */
  setupGameRowLiveGlowClass(game) {
    if (!game || !this.navigatorGameReady(game) || this.navigatorGameIsComplete(game)) return '';
    const idx = Number(this.setupGameIndex(game));
    const currentIndex = Number(this.session?.currentGameIndex || 0);
    if (idx !== currentIndex || !this.navigatorGameInProgress(game)) return '';
    return [
      'ring-2 ring-sky-400/35',
      'shadow-[0_0_0_1px_rgba(14,165,233,0.12),0_0_24px_-6px_rgba(14,165,233,0.18)]',
      'dark:ring-sky-400/30',
      'dark:shadow-[0_0_0_1px_rgba(56,189,248,0.1),0_0_28px_-8px_rgba(56,189,248,0.16)]',
    ].join(' ');
  },

  setupGameStatusClass(game) {
    if (!game) {
      return 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/90 dark:bg-neutral-800 dark:text-neutral-200 dark:ring-neutral-600/50';
    }
    if (!this.navigatorGameReady(game)) {
      return 'bg-amber-100 text-amber-900 ring-1 ring-amber-300/70 dark:bg-amber-900/35 dark:text-amber-100 dark:ring-amber-700/45';
    }
    if (this.navigatorGameIsComplete(game)) {
      return this.setupGameCompleteStatusPillClass();
    }
    const idx = Number(this.setupGameIndex(game));
    const currentIndex = Number(this.session?.currentGameIndex || 0);
    if (idx === currentIndex) {
      return this.navigatorGameInProgress(game)
        ? 'bg-sky-100 text-sky-900 ring-1 ring-sky-400/75 dark:bg-sky-900/40 dark:text-sky-100 dark:ring-sky-600/45'
        : 'bg-amber-100 text-amber-900 ring-1 ring-amber-300/70 dark:bg-amber-900/35 dark:text-amber-100 dark:ring-amber-700/45';
    }
    if (this.navigatorGameHasStarted(game)) {
      return 'bg-violet-100 text-violet-900 ring-1 ring-violet-300/75 dark:bg-violet-900/40 dark:text-violet-100 dark:ring-violet-600/45';
    }
    return 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/90 dark:bg-neutral-800 dark:text-neutral-200 dark:ring-neutral-600/50';
  },

  navigatorGameStatusClass(game, index) {
    const active = index === Number(this.session?.currentGameIndex || 0);
    const status = this.navigatorGameStatus(game);
    if (active) {
      if (status === 'Unassigned') return 'bg-amber-100 text-amber-900 ring-1 ring-amber-300/70 dark:bg-amber-900/30 dark:text-amber-100 dark:ring-amber-700/40';
      if (status === 'Complete') return 'bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300/80 dark:bg-emerald-500/25 dark:text-emerald-100 dark:ring-emerald-500/40';
      return 'bg-white/15 text-white';
    }
    if (status === 'Unassigned') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200';
    if (status === 'Complete') return 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300/80 dark:bg-emerald-400/35 dark:text-emerald-950 dark:ring-emerald-300/70';
    if (status === 'In progress' || status === 'Playing') return 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200';
    if (status === 'Resume') return 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200';
    if (status === 'Ready') return 'bg-slate-100 text-slate-700 dark:bg-neutral-800 dark:text-neutral-200';
    return 'bg-black/5 text-slate-600 dark:bg-white/5 dark:text-neutral-300';
  },

  navigatorPlayerNames(game, side) {
    const ids = ((side === 'home' ? game?.homePlayers : game?.awayPlayers) || []).map((id) => String(id || '')).filter(Boolean);
    if (!ids.length) return '';
    return ids.map((playerId) => this.playerName(playerId)).filter(Boolean).join(' / ');
  },

  navigatorMatchup(game) {
    const home = this.navigatorPlayerNames(game, 'home');
    const away = this.navigatorPlayerNames(game, 'away');
    if (!home && !away) return 'Awaiting player assignment';
    if (home && away) return `${home} vs ${away}`;
    return `${home || away} assigned`;
  },

  venueKioskFixtureHubMode() {
    if (this.scoringWindow) return false;
    if (this.forceScoringWindow || isScoringWindowMode()) return false;
    try {
      const sp = new URLSearchParams(String(globalThis.location?.search || ''));
      if (['1', 'true', 'yes'].includes(String(sp.get('mobile') || '').toLowerCase())) return false;
    } catch {
      /* ignore */
    }
    return !!(this.embedMode && this.fromVenueKiosk);
  },

  kioskHubCanEnterScoring() {
    if (this.fixtureSessionIsFinalized()) return false;
    if (!this.setupReady()) return false;
    return true;
  },

  kioskHubScoreLiveHint() {
    if (this.fixtureSessionIsFinalized()) return 'This fixture is finished.';
    if (!this.setupReady()) return this.setupBlockingMessage?.() || 'Complete fixture setup before scoring.';
    return '';
  },

  kioskHubPendingMatchLine() {
    if (!this.hasPendingNextMatch()) return '';
    const next = this.pendingNextGame();
    if (!next) return '';
    return `${this.pendingFinishedMatchWinnerLine()} Tap Continue on the scorer or open ${this.gameLabel(next)} below.`;
  },

  kioskHubSpotlightGame() {
    const games = this.kioskHubGames();
    if (!games.length) return null;
    const curIdx = Math.max(0, Number(this.session?.currentGameIndex ?? 0));
    const cur = games[curIdx];
    if (cur && !this.navigatorGameIsComplete(cur)) return cur;
    for (let i = 0; i < games.length; i += 1) {
      const game = games[i];
      if (!this.navigatorGameIsComplete(game)) return game;
    }
    return cur || games[games.length - 1];
  },

  kioskHubSpotlightGameIndex() {
    const games = this.kioskHubGames();
    const spot = this.kioskHubSpotlightGame();
    if (!spot) return 0;
    const idx = games.indexOf(spot);
    return idx >= 0 ? idx : 0;
  },

  kioskHubGameLabel(game, index) {
    const type = String(game?.type || 'singles').toLowerCase();
    if (type === 'beer') return 'Beer leg';
    if (type === 'doubles') return `Doubles ${Number(game?.order || index + 1)}`;
    return `Singles ${Number(game?.order || index + 1)}`;
  },

  kioskHubProgramStatusLabel(game, index) {
    if (!game) return '';
    if (this.navigatorGameIsComplete(game)) return 'Done';
    const pendingIdx = Number(this.session?.pendingAdvanceToGameIndex);
    if (this.hasPendingNextMatch() && pendingIdx === index) return 'Ready';
    if (this.navigatorGameInProgress(game)) return 'Live';
    if (index === this.kioskHubSpotlightGameIndex()) return 'Next';
    return 'Queued';
  },

  kioskHubProgramActionLabel(game, index) {
    if (!game) return '';
    if (this.navigatorGameIsComplete(game)) return 'View';
    if (this.navigatorGameInProgress(game)) return 'Resume';
    const pendingIdx = Number(this.session?.pendingAdvanceToGameIndex);
    if (this.hasPendingNextMatch() && pendingIdx === index) return 'Start';
    return 'Start';
  },

  kioskHubProgramStatusClass(game, index) {
    if (this.navigatorGameIsComplete(game)) {
      return 'bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-500/25';
    }
    if (this.navigatorGameInProgress(game)) {
      return 'bg-sky-500/20 text-sky-100 ring-1 ring-sky-400/35';
    }
    if (index === this.kioskHubSpotlightGameIndex()) {
      return 'bg-amber-500/15 text-amber-100 ring-1 ring-amber-400/30';
    }
    return 'bg-white/5 text-neutral-400 ring-1 ring-white/10';
  },

  gameWinnerSide(game) {
    if (!game || !this.navigatorGameIsComplete(game)) return '';
    const wins = this.gameLegWins(game);
    const needed = this.gameLegsNeeded(game);
    if (wins.home >= needed) return 'home';
    if (wins.away >= needed) return 'away';
    return '';
  },

  gameWinnerTeamName(game) {
    const side = this.gameWinnerSide(game);
    return side ? this.teamName(side) : '';
  },

  kioskHubGameRowClass(game, index) {
    const active = index === Number(this.session?.currentGameIndex || 0);
    if (this.navigatorGameIsComplete(game)) {
      return 'border-emerald-700/40 bg-emerald-950/20';
    }
    if (active) {
      return 'border-sky-500/50 bg-sky-950/30 ring-1 ring-sky-500/25';
    }
    return 'border-neutral-700/45 bg-neutral-950/30';
  },

  async openVenueKioskFixtureScorer() {
    if (!this.setupReady()) {
      Alpine.store('toastr')?.info?.('Fixture setup is not ready yet', 2500);
      return;
    }
    if (typeof this.flushPendingSetupSaves === 'function') {
      await this.flushPendingSetupSaves({ silent: false });
    }
    this.scoringWindow = true;
    this.applyDedicatedChromeMode(true);
    scrollWindowToTop();
    if (!this.fixtureSessionIsFinalized() && !this.currentController()) {
      const claimed = await this.ensureControllerClaim(false);
      if (!claimed && !this.isControllerOwner()) {
        this.scoringWindow = false;
        this.applyDedicatedChromeMode(false);
        Alpine.store('toastr')?.error?.('Could not take scoring control. Sign in on the venue tablet or restart from the fixture wizard.', 4500);
        return;
      }
    }
    this.ensureEventStream();
    this.ensureHeartbeatLoop();
    this.ensureReplayLoop();
    void this.flushPendingEvents();
  },

  returnVenueKioskFixtureHub() {
    this.scoringWindow = false;
    this.applyDedicatedChromeMode(false);
    this.stopHeartbeatLoop();
    scrollWindowToTop();
  },

  shouldWarnOnKioskHubSwitch(targetIndex, targetGame = null) {
    const idx = Number(targetIndex);
    const currentIdx = Number(this.session?.currentGameIndex ?? 0);
    if (!Number.isInteger(idx) || idx < 0 || idx === currentIdx) return false;
    const currentGame = this.kioskHubGames()[currentIdx];
    if (!currentGame || this.navigatorGameIsComplete(currentGame)) return false;
    if (!this.navigatorGameInProgress(currentGame)) return false;
    const nextGame = targetGame || this.kioskHubGames()[idx];
    return !!nextGame && !this.navigatorGameIsComplete(nextGame);
  },

  cancelKioskHubSwitchConfirm() {
    this.kioskHubSwitchConfirmOpen = false;
    this.kioskHubSwitchTargetIndex = -1;
  },

  async confirmKioskHubSwitchGame() {
    const idx = Number(this.kioskHubSwitchTargetIndex);
    this.cancelKioskHubSwitchConfirm();
    if (!Number.isInteger(idx) || idx < 0) return;
    await this.kioskHubContinueSelectGame(idx);
  },

  async kioskHubContinueSelectGame(idx) {
    const game = this.kioskHubGames()[idx];
    if (!game) return;
    if (this.navigatorGameIsComplete(game)) {
      await this.goToGame(idx);
      return;
    }

    const pendingIdx = Number(this.session?.pendingAdvanceToGameIndex);
    if (this.hasPendingNextMatch() && pendingIdx === idx) {
      const ok = await this.acceptPendingNextMatch();
      if (!ok) return;
    } else if (idx !== Number(this.session?.currentGameIndex ?? 0)) {
      await this.goToGame(idx);
    }

    await this.openVenueKioskFixtureScorer();
  },

  async kioskHubSelectGame(index) {
    const idx = Number(index);
    if (Number.isNaN(idx) || idx < 0) return;
    const game = this.kioskHubGames()[idx];
    if (!game) return;
    if (this.shouldWarnOnKioskHubSwitch(idx, game)) {
      this.kioskHubSwitchTargetIndex = idx;
      this.kioskHubSwitchConfirmOpen = true;
      return;
    }
    await this.kioskHubContinueSelectGame(idx);
  },

  kioskHubGames() {
    return Array.isArray(this.session?.games) ? this.session.games : [];
  },

  navigatorLegSummary(game) {
    if (!this.navigatorGameHasStarted(game)) return '';
    const wins = this.gameLegWins(game);
    return `Legs ${wins.home}-${wins.away} (first to ${this.gameLegsNeeded(game)})`;
  },

  currentGameMetaLabel() {
    const game = this.currentGame();
    return `${game?.format || '501'} / Best of ${game?.bestOfLegs || 3}`;
  },

  currentGameCardTitle() {
    const game = this.kioskHubSpotlightGame();
    if (!game) return 'Next game';
    if (this.navigatorGameIsComplete(game)) return 'Finished game';
    if (this.navigatorGameInProgress(game)) return 'In progress';
    return 'Next game';
  },

  currentGameMatchup() {
    const game = this.venueKioskFixtureHubMode() ? this.kioskHubSpotlightGame() : this.currentGame();
    if (!game) return '';
    return this.navigatorMatchup(game);
  },

  kioskHubSpotlightLegWins() {
    return this.gameLegWins(this.kioskHubSpotlightGame());
  },

  kioskHubSpotlightLegsNeeded() {
    return this.gameLegsNeeded(this.kioskHubSpotlightGame());
  },

  sidePlayerNames(side) {
    const ids = (side === 'home' ? this.selectedHomePlayers : this.selectedAwayPlayers).filter(Boolean);
    const names = ids.map((playerId) => this.playerName(playerId)).filter(Boolean);
    return names.length ? names : [this.teamName(side)];
  },

  playerRecord(playerId) {
    const all = [...this.roster('home'), ...this.roster('away'), ...(this.allPlayers || [])];
    return all.find((item) => String(item._id) === String(playerId)) || null;
  },

  playerTeamName(player) {
    if (!player) return '';
    return player?.team?.name
      || this.teams.find((team) => String(team?._id || '') === String(player?.registeredTeamId || ''))?.name
      || '';
  },

  borrowedPlayerIdsForSide(side) {
    const ids = new Set();
    for (const game of this.setupGames()) {
      for (const entry of this.setupPlayerMeta(game, side)) {
        if (entry?.borrowed && entry?.playerId) ids.add(String(entry.playerId));
      }
    }
    return ids;
  },

  openBorrowPicker(target) {
    if (target?.kind === 'fixture-setup' && !this.canEditSetupGameSide(target?.gameIndex, target?.side)) return;
    this.borrowPicker.target = target;
    this.borrowPicker.side = target?.side || 'home';
    this.borrowPicker.search = '';
    this.borrowPicker.open = true;
  },

  closeBorrowPicker() {
    this.borrowPicker.open = false;
    this.borrowPicker.side = 'home';
    this.borrowPicker.search = '';
    this.borrowPicker.target = null;
  },

  borrowPickerTitle() {
    const target = this.borrowPicker?.target;
    if (!target) return 'Borrow Player';
    const game = this.session?.games?.[target.gameIndex];
    if (!game) return 'Borrow Player';
    const sideLabel = target.side === 'home' ? 'Home' : 'Away';
    const gameLabel = this.setupLabel(game);
    if (this.setupExpectedPlayers(game) === 1) return `Borrow Player for ${sideLabel} ${gameLabel}`;
    return `Borrow Player for ${sideLabel} ${gameLabel} Player ${Number(target.slotIndex || 0) + 1}`;
  },

  borrowedPlayerCandidates() {
    const homeTeamId = String(this.session?.homeTeamId || this.fixture?.homeTeamId || '');
    const awayTeamId = String(this.session?.awayTeamId || this.fixture?.awayTeamId || '');
    const search = String(this.borrowPicker.search || '').trim().toLowerCase();
    const side = this.borrowPicker?.side || 'home';
    const oppositeSide = side === 'home' ? 'away' : 'home';
    const oppositeBorrowedIds = this.borrowedPlayerIdsForSide(oppositeSide);
    return (this.allPlayers || [])
      .filter((player) => {
        const teamId = String(player?.registeredTeamId || '');
        const st = String(player?.status || '').toLowerCase();
        if (st === 'pool') {
          if (!teamId) return true;
          return teamId !== homeTeamId && teamId !== awayTeamId;
        }
        return !!teamId && teamId !== homeTeamId && teamId !== awayTeamId;
      })
      .filter((player) => {
        const playerId = String(player?._id || '');
        return playerId && !oppositeBorrowedIds.has(playerId);
      })
      .filter((player) => {
        if (!search) return true;
        const haystack = `${player.fullName || ''} ${player.nickname || ''} ${this.playerTeamName(player)}${String(player?.status || '').toLowerCase() === 'pool' ? ' spare pool' : ''}`.toLowerCase();
        return haystack.includes(search);
      })
      .slice(0, 50);
  },

  async assignBorrowedPlayer(player) {
    const target = this.borrowPicker?.target;
    if (!target || !player?._id) return;
    if (target?.kind === 'fixture-setup' && !this.canEditSetupGameSide(target?.gameIndex, target?.side)) return;
    const game = this.session?.games?.[target.gameIndex];
    if (!game) return;
    const side = target.side;
    const oppositeSide = side === 'home' ? 'away' : 'home';
    const playerId = String(player._id || '');
    if (this.borrowedPlayerIdsForSide(oppositeSide).has(playerId)) {
      this.$store?.toastr?.error?.('That borrowed player is already assigned to the opposite side.', 3000);
      return;
    }
    const players = this.setupPlayerIds(game, side);
    players[target.slotIndex] = playerId;
    const deduped = [];
    for (const currentId of players) {
      if (!currentId || deduped.includes(currentId)) continue;
      deduped.push(currentId);
    }
    while (deduped.length < this.setupExpectedPlayers(game)) deduped.push('');
    const finalPlayers = deduped.filter(Boolean);
    const insertedIndex = finalPlayers.indexOf(playerId);
    const teamName = String(player?.status || '').toLowerCase() === 'pool'
      ? 'Spare pool'
      : this.playerTeamName(player);
    const playerMeta = this.setupPlayerMetaPayload(game, side, finalPlayers, {
      [insertedIndex]: {
        borrowed: true,
        borrowedFromTeamId: String(player.registeredTeamId || ''),
        borrowedFromTeamName: teamName,
      },
    });
    this.applyLocalSetupPlayers(target.gameIndex, side, finalPlayers, playerMeta);
    this.scheduleSetupSave(target.gameIndex, side, finalPlayers, playerMeta);
    this.closeBorrowPicker();
  },

  async cancelBorrowedSelection(target) {
    if (!target) return;
    if (target?.kind === 'fixture-setup' && !this.canEditSetupGameSide(target?.gameIndex, target?.side)) return;
    const game = this.session?.games?.[target.gameIndex];
    if (!game) return;
    const side = target.side;
    const players = this.setupPlayerIds(game, side);
    players[target.slotIndex] = '';
    const finalPlayers = players.filter(Boolean);
    const playerMeta = this.setupPlayerMetaPayload(game, side, finalPlayers);
    this.applyLocalSetupPlayers(target.gameIndex, side, finalPlayers, playerMeta);
    this.scheduleSetupSave(target.gameIndex, side, finalPlayers, playerMeta);
  },

  sidePlayerLabel(side) {
    const game = this.currentGame();
    const type = String(game?.type || 'singles');
    const names = this.sidePlayerNames(side);
    if (type === 'beer') return names.join(', ');
    if (type === 'doubles') return names.join(' & ');
    return names[0] || this.teamName(side);
  },

  sidePlayerAvatar(side) {
    const ids = (side === 'home' ? this.selectedHomePlayers : this.selectedAwayPlayers).filter(Boolean);
    const first = ids[0];
    return resolvePlayerAvatarUrl(this.playerRecord(first));
  },

  currentMatchupLabel() {
    const game = this.currentGame();
    const type = String(game?.type || 'singles');
    const home = this.sidePlayerNames('home').join(type === 'doubles' ? ' & ' : ', ');
    const away = this.sidePlayerNames('away').join(type === 'doubles' ? ' & ' : ', ');
    if (type === 'beer') return `${this.teamName('home')} vs ${this.teamName('away')}`;
    return `${home} vs ${away}`;
  },

  fixtureBriefLabel() {
    return `${this.fixture?.homeTeamName || 'Home'} vs ${this.fixture?.awayTeamName || 'Away'}`;
  },

  teamLogo(side) {
    const fallback = '/assets/branding/favicon-32.png';
    if (side === 'home') return this.fixture?.homeTeamLogo || this.fixture?.home?.logo || fallback;
    return this.fixture?.awayTeamLogo || this.fixture?.away?.logo || fallback;
  },

  syncScorerTopBarViewport() {
    try {
      this.scorerMobileTopBar = window.matchMedia('(max-width:639px)').matches;
    } catch {
      this.scorerMobileTopBar = false;
    }
  },

  scorerTopBarRowPaddingStyle() {
    if (!this.scoringWindow) return '';
    return 'padding-top:0.2em;padding-bottom:0.2em;';
  },

  currentGameBadge() {
    const game = this.currentGame();
    const order = Number(game?.order || 0);
    const type = String(game?.type || 'singles');
    if (type === 'doubles') return `D${order}`;
    if (type === 'beer') return 'Beer';
    return `S${order}`;
  },

  /** Short team label for compact console header (no HTML). */
  consoleTeamShortLabel(side) {
    const raw = String(
      side === 'home' ? (this.fixture?.homeTeamName || 'Home') : (this.fixture?.awayTeamName || 'Away'),
    ).trim();
    if (!raw) return side === 'home' ? 'HOME' : 'AWAY';
    const squashed = raw.replaceAll(/\s+/g, '');
    if (squashed.length <= 5) return squashed.toUpperCase();
    return `${squashed.slice(0, 4).toUpperCase()}…`;
  },

  playerName(playerId) {
    const player = this.playerRecord(playerId);
    if (!player) return 'Unassigned';
    const base = String(player.fullName || player.nickname || '').trim() || 'Unassigned';
    const st = String(player?.status || '').toLowerCase();
    if (st === 'reserve') return `${base} (reserve)`;
    if (st === 'pool') return `${base} (spare pool)`;
    return base;
  },

  async openDedicatedScoringWindow() {
    if (!this.assignmentSetupReady()) {
      Alpine.store('toastr').info('Complete both team setup cards before opening the scorer', 2500);
      return;
    }
    await this.flushPendingSetupSaves({ silent: false });
    const url = `/admin/fixtures/${this.fixtureId}/scorer`;
    const handle = window.open(url, `pdl-fixture-scorer-${this.fixtureId}`, 'popup=yes,width=1440,height=980,resizable=yes,scrollbars=yes');
    if (!handle) {
      try {
        window.location.assign(url);
      } catch {
        window.location.href = url;
      }
    }
  },

  /** Match Console: open dedicated scorer in a normal tab (same PC / Win32 / laptop). */
  async openScorerInNewTab() {
    const flushed = await this.flushPendingSetupSaves({ silent: false });
    if (!flushed) return;
    if (!this.assignmentSetupReady()) {
      Alpine.store('toastr').info('Complete both team setup cards before opening the scorer', 2500);
      return;
    }
    const url = this.dedicatedScorerPageUrl();
    const handle = window.open(url, `pdl-fixture-scorer-${this.fixtureId}`, 'popup=yes,width=1440,height=980,resizable=yes,scrollbars=yes');
    if (!handle) {
      try {
        window.location.assign(url);
      } catch {
        window.location.href = url;
      }
    } else {
      handle.opener = null;
    }
  },

  teamName(side) {
    return side === 'home' ? (this.fixture?.homeTeamName || 'Home') : (this.fixture?.awayTeamName || 'Away');
  },

  tvKioskPublicUrl() {
    return `${preferredAppOrigin()}/tv`;
  },

  async pairTvToSession() {
    this.tvPairError = '';
    const digits = String(this.tvPairCode || '').replace(/\D/g, '');
    const code = digits.length > 4 ? digits.slice(-4) : digits.padStart(4, '0');
    if (!code || code.length < 4 || !this.session?._id) return;
    this.tvPairBusy = true;
    try {
      const res = await apiFetch('/tv-sessions/pair', {
        method: 'POST',
        body: { pairCode: code, fixtureSessionId: String(this.session._id) },
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.message || 'Pair failed');
      this.tvPairCode = '';
      Alpine.store('toastr').success('TV paired — the display should switch to this match shortly.', 4000);
    } catch (e) {
      this.tvPairError = e.message || 'Pair failed';
    } finally {
      this.tvPairBusy = false;
    }
  },

  async openViewScreen() {
    const flushed = await this.flushPendingSetupSaves({ silent: false });
    if (!flushed) return;
    if (!this.setupReady()) {
      Alpine.store('toastr').info(
        this.assignmentSetupReady()
          ? 'Both team captains must confirm ready before opening the board'
          : 'Complete both team setup cards before opening the board',
        2800,
      );
      return;
    }
    const url = `/admin/fixtures/${this.fixtureId}/board`;
    window.open(url, `pdl-fixture-board-${this.fixtureId}`, 'popup=yes,width=1600,height=900,resizable=yes,scrollbars=yes');
  },

  async openViewDebugScreen() {
    const flushed = await this.flushPendingSetupSaves({ silent: false });
    if (!flushed) return;
    if (!this.setupReady()) {
      Alpine.store('toastr').info(
        this.assignmentSetupReady()
          ? 'Both team captains must confirm ready before opening board debug'
          : 'Complete both team setup cards before opening board debug',
        2800,
      );
      return;
    }
    const url = `/admin/fixtures/${this.fixtureId}/board?debug=1`;
    window.open(url, `pdl-fixture-board-debug-${this.fixtureId}`, 'popup=yes,width=1440,height=980,resizable=yes,scrollbars=yes');
  },

  viewScreenUrl() {
    return `${preferredAppOrigin()}/admin/fixtures/${this.fixtureId}/board`;
  },

  scorerWindowUrl() {
    return `${preferredAppOrigin()}/admin/fixtures/${this.fixtureId}/scorer`;
  },

  async renderScorerQr() {
    if (this.scoringWindow) return;
    const mount = this.$refs?.scorerQr;
    if (!mount) return;
    const data = this.scorerWindowUrl();
    if (!this.scorerQrCanvas) {
      this.scorerQrCanvas = document.createElement('canvas');
      this.scorerQrCanvas.width = 120;
      this.scorerQrCanvas.height = 120;
      this.scorerQrCanvas.className = 'block h-auto max-h-full max-w-full w-full rounded-md';
      mount.innerHTML = '';
      mount.appendChild(this.scorerQrCanvas);
    }
    try {
      await QRCode.toCanvas(this.scorerQrCanvas, data, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 120,
        color: {
          dark: '#020617',
          light: '#ffffff',
        },
      });
    } catch (error) {
      console.error('Failed to render scorer QR', error);
    }
  },

  async toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch { }
  },

  async castViewScreen() {
    const flushed = await this.flushPendingSetupSaves({ silent: false });
    if (!flushed) return;
    if (!this.setupReady()) {
      Alpine.store('toastr').info(
        this.assignmentSetupReady()
          ? 'Both team captains must confirm ready before casting the board'
          : 'Complete both team setup cards before casting the board',
        2800,
      );
      return;
    }
    const boardUrl = this.viewScreenUrl();
    try {
      if ('PresentationRequest' in window) {
        const request = new window.PresentationRequest([boardUrl]);
        await request.start();
        Alpine.store('toastr').success('View screen cast started', 2500);
        return;
      }
    } catch {
      // fall through to mobile/system share and fallback options
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: `${this.fixture?.homeTeamName || 'Home'} vs ${this.fixture?.awayTeamName || 'Away'} view screen`,
          text: 'Open the live fixture view screen',
          url: boardUrl,
        });
        Alpine.store('toastr').success('View screen shared', 2500);
        return;
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(boardUrl);
        this.openViewScreen();
        Alpine.store('toastr').success('View screen opened and link copied', 3000);
        return;
      }
    } catch {
      // final fallback below
    }

    this.openViewScreen();
    Alpine.store('toastr').success('View screen opened in a new window', 2500);
  },

  async close() {
    await this.flushPendingSetupSaves({ silent: true });
    await this.releaseControllerClaim();
    this.teardown();
    if (this.scoringWindow && window.opener) {
      window.close();
      return;
    }
    routerNavigate(`/admin/fixtures/${this.fixtureId}`);
  },

  jumpTargets() {
    return [
      { key: 'scorer', label: 'Scorer', ref: 'scorerPanel' },
      { key: 'history', label: 'Throw History', ref: 'historyPanel' },
      { key: 'navigator', label: 'Game Navigator', ref: 'navigatorPanel' },
      { key: 'actions', label: 'Match Actions', ref: 'actionsPanel' },
      ...(!this.scoringWindow ? [{ key: 'players', label: 'Game Players', ref: 'playersPanel' }] : []),
    ];
  },

  jumpTo(refName) {
    this.jumpMenuOpen = false;
    this.quickConfigOpen = false;
    if (refName === 'historyPanel' && typeof this.openScorerMobileHistory === 'function') {
      this.openScorerMobileHistory();
      return;
    }
    const target = this.$refs?.[refName];
    if (!target || typeof target.getBoundingClientRect !== 'function') return;
    const offsetTop = window.scrollY + target.getBoundingClientRect().top - 60;
    window.scrollTo({
      top: Math.max(0, offsetTop),
      behavior: 'smooth',
    });
  },

  toggleFixtureScorerAside() {
    this.fixtureScorerAsideOpen = !this.fixtureScorerAsideOpen;
  },

  fixtureScorerAsideVisible() {
    if (!this.scoringWindow) return true;
    try {
      if (window.matchMedia('(min-width: 1280px)').matches) return true;
    } catch { /* ignore */ }
    return this.fixtureScorerAsideOpen;
  },

  remainingForSide(side) {
    const leg = this.currentLeg();
    const start = this.startingScoreForCurrentGame();
    if (!leg) return start;
    const raw = side === 'home' ? leg.homeRemaining : leg.awayRemaining;
    if (raw === null || raw === undefined || raw === '') return start;
    const n = Number(raw);
    return Number.isFinite(n) ? n : start;
  },

  legDartsFractionDisplay() {
    return String(this.currentLegDartsThrown());
  },

  fixtureScoreboardCenterSubline() {
    const wins = this.gameLegWins(this.currentGame());
    const need = this.gameLegsNeeded(this.currentGame());
    const max = Math.max(wins.home, wins.away);
    const left = Math.max(0, need - max);
    if (left <= 0) return 'Deciding leg';
    return left === 1 ? '1 leg for game' : `${left} legs for game`;
  },

  fixtureScoreboardCenterDetail() {
    const game = this.currentGame();
    if (!game) return '';
    const label = this.gameLabel(game);
    const badge = this.currentGameBadge();
    return badge && label ? `${badge} · ${label}` : (label || badge || '');
  },

  fixtureVisitPillsForSide(side) {
    return this.lastVisitsForSide(side, 3).map(({ visit }) => this.visitCompactDisplay(visit));
  },

  fixturePlayerIdForSide(side) {
    const ids = (side === 'home' ? this.selectedHomePlayers : this.selectedAwayPlayers).filter(Boolean);
    return ids[0] ? String(ids[0]) : '';
  },

  statsCarouselRoster() {
    const out = [];
    const pushSide = (side) => {
      const ids = (side === 'home' ? this.selectedHomePlayers : this.selectedAwayPlayers).filter(Boolean);
      for (const id of ids) {
        const pid = String(id);
        out.push({
          id: pid,
          name: this.playerName(pid),
          side,
          avatarUrl: resolvePlayerAvatarUrl(this.playerRecord(pid)),
        });
      }
    };
    pushSide('home');
    pushSide('away');
    return out;
  },

  computePlayerMatchStats(playerRef) {
    const pid = String(playerRef || '');
    if (!pid) return null;
    let darts = 0;
    let points = 0;
    let n180 = 0;
    let n140 = 0;
    let n100 = 0;
    let checkouts = 0;
    let legsWon = 0;
    const homeIds = new Set((this.selectedHomePlayers || []).map(String));
    const playerSide = homeIds.has(pid) ? 'home' : 'away';
    const game = this.currentGame();
    for (const leg of game?.legs || []) {
      if (String(leg?.winnerSide || '') === playerSide) legsWon += 1;
      for (const visit of leg?.visits || []) {
        if (String(visit?.playerId || '') !== pid) continue;
        const dv = Math.max(1, Math.min(3, Number(visit.darts) || 3));
        darts += dv;
        if (visit.busted) continue;
        const sc = Math.min(180, Math.max(0, Number(visit.score) || 0));
        points += sc;
        if (visit.checkout) checkouts += 1;
        if (sc === 180) n180 += 1;
        else if (sc >= 140) n140 += 1;
        else if (sc >= 100) n100 += 1;
      }
    }
    if (!legsWon) {
      const wins = this.gameLegWins(game);
      legsWon = playerSide === 'home' ? wins.home : wins.away;
    }
    const threeDartAvg = darts > 0 ? (points / darts) * 3 : null;
    return { darts, points, threeDartAvg, n180, n140, n100, checkouts, legsWon };
  },

  ...createScorerMatchScoreboardBindings({
    isLive() {
      return !this.fixtureScoringFrozen() && this.canScore();
    },
    sideRole(side) {
      return side === 'home'
        ? (this.fixture?.homeTeamName || 'Home')
        : (this.fixture?.awayTeamName || 'Away');
    },
    legsForSide(side) {
      const wins = this.gameLegWins(this.currentGame());
      return side === 'home' ? wins.home : wins.away;
    },
    centerSubline() {
      return this.fixtureScoreboardCenterSubline();
    },
    centerDetail() {
      return this.fixtureScoreboardCenterDetail();
    },
    showVisitPills() {
      return true;
    },
    visitPillsForSide(side) {
      return this.fixtureVisitPillsForSide(side);
    },
    onTapAvatar(side) {
      const playerId = String(this.activeThrower[side] || this.defaultThrowerFor(side) || '');
      if (!playerId) return;
      this.openPlayerStatsForPlayer(playerId);
    },
  }),

  ...createScorerPlayerStatsCarouselMethods(),
}));
